import { useState, useEffect, useRef } from "react";
import { db, auth } from "./firebase";
import { collection, onSnapshot, query, orderBy, addDoc, serverTimestamp, limit } from "firebase/firestore";
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, updateProfile } from "firebase/auth";
import { Home, Search, Bookmark, BookmarkCheck, LayoutDashboard, MessageSquare, LogIn, LogOut, Bell, X, Menu, Tag, Calculator, Plus, BarChart2, Settings, FileText, CheckCircle, XCircle, ArrowLeft, ShoppingBag, Send, ChevronRight, Package, CornerDownLeft, Rocket, Bot, Monitor, Smartphone, Globe, Layers, Lock, Star, Mail, Users, Database, Folder, TrendingUp, CreditCard, DollarSign, Clock, BookOpen, Cpu, Shield, AlertCircle, BadgeCheck, CircleDollarSign, Gavel, BarChart, ShoppingCart, Palette, Link } from "lucide-react";
import SoftSellBuilder from "./src/softsell-builder.jsx";

/* ─── FONTS ─────────────────────────────────────────────────────── */
const FONT_URL = "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600&display=swap";

/* ─── CSS ────────────────────────────────────────────────────────── */
const CSS = `
@import url('${FONT_URL}');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html { font-size: 16px; scroll-behavior: smooth; }
body { font-family: 'Inter', sans-serif; background: #0D1117; color: #E2E8F0; -webkit-font-smoothing: antialiased; overflow-x: hidden; }

:root {
  --bg: #0D1117;
  --surface: #161B24;
  --surface2: #1E2533;
  --border: #2A3242;
  --accent: #5B4FE8;
  --accent-light: #7B72F0;
  --accent-dim: rgba(91,79,232,0.18);
  --accent-glow: rgba(91,79,232,0.28);
  --green: #22C55E;
  --green-dim: rgba(34,197,94,0.12);
  --red: #EF4444;
  --red-dim: rgba(239,68,68,0.12);
  --gold: #F59E0B;
  --gold-dim: rgba(245,158,11,0.12);
  --text: #E2E8F0;
  --muted: #8892A0;
  --display: 'Space Grotesk', sans-serif;
  --nav-h: 60px;
  --bottom-nav-h: 64px;
}

::-webkit-scrollbar { width: 5px; height: 5px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: var(--border); border-radius: 3px; }

/* ── Layout ──────────────────────────────────────────────────────── */
.app { min-height: 100vh; display: flex; flex-direction: column; }
.container { max-width: 1180px; margin: 0 auto; padding: 0 20px; width: 100%; }
.page-pad { padding: 32px 20px 80px; max-width: 1180px; margin: 0 auto; }
@media (max-width: 768px) { .page-pad { padding: 20px 16px 96px; } }

/* ── Nav ─────────────────────────────────────────────────────────── */
.nav { position: sticky; top: 0; z-index: 200; height: var(--nav-h); background: rgba(13,17,23,0.94); backdrop-filter: blur(14px); border-bottom: 1px solid var(--border); }
.nav-inner { display: flex; align-items: center; justify-content: space-between; height: 100%; padding: 0 20px; max-width: 1180px; margin: 0 auto; }
.nav-logo { font-family: var(--display); font-size: 1.2rem; font-weight: 700; color: #fff; cursor: pointer; display: flex; align-items: center; gap: 8px; user-select: none; }
.nav-logo .accent { color: var(--accent); }
.nav-logo .beta { font-size: 0.62rem; background: var(--surface2); border: 1px solid var(--border); border-radius: 4px; padding: 2px 6px; color: var(--muted); font-family: 'Inter', sans-serif; font-weight: 600; }
.nav-links { display: flex; align-items: center; gap: 4px; }
.nav-link { background: none; border: none; color: var(--muted); font-family: 'Inter', sans-serif; font-size: 0.875rem; padding: 7px 12px; border-radius: 7px; cursor: pointer; transition: color 0.15s, background 0.15s; }
.nav-link:hover { color: var(--text); background: var(--surface); }
.nav-cta { background: var(--accent); color: #fff; border: none; font-family: 'Inter', sans-serif; font-size: 0.875rem; font-weight: 600; padding: 7px 16px; border-radius: 8px; cursor: pointer; transition: background 0.15s, box-shadow 0.15s; }
.nav-cta:hover { background: var(--accent-light); box-shadow: 0 0 20px var(--accent-glow); }
.nav-icon-btn { background: none; border: none; color: var(--muted); cursor: pointer; padding: 7px; border-radius: 7px; display: flex; align-items: center; font-size: 1.1rem; position: relative; transition: color 0.15s; }
.nav-icon-btn:hover { color: var(--text); background: var(--surface); }
.nav-badge { position: absolute; top: 4px; right: 4px; width: 8px; height: 8px; background: var(--red); border-radius: 50%; border: 2px solid var(--bg); }
.hamburger { display: none; background: none; border: none; color: var(--text); cursor: pointer; font-size: 1.4rem; padding: 4px; }
@media (max-width: 768px) { .nav-links { display: none; } .hamburger { display: block; } }

/* ── Mobile Drawer ───────────────────────────────────────────────── */
.drawer-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 300; backdrop-filter: blur(4px); }
.drawer { position: fixed; top: 0; right: 0; bottom: 0; width: 260px; background: var(--surface); z-index: 301; padding: 20px; display: flex; flex-direction: column; gap: 8px; border-left: 1px solid var(--border); }
.drawer-close { align-self: flex-end; background: none; border: none; color: var(--muted); font-size: 1.4rem; cursor: pointer; padding: 4px; margin-bottom: 8px; }
.drawer-link { background: none; border: none; color: var(--text); font-family: 'Inter', sans-serif; font-size: 0.95rem; padding: 12px 14px; border-radius: 9px; cursor: pointer; text-align: left; width: 100%; display: flex; align-items: center; gap: 10px; transition: background 0.12s; }
.drawer-link:hover { background: var(--surface2); }
.drawer-link.primary { background: var(--accent); color: #fff; justify-content: center; font-weight: 600; margin-top: 8px; }
.drawer-link.primary:hover { background: var(--accent-light); }

/* ── Bottom Nav (mobile) ─────────────────────────────────────────── */
.bottom-nav { display: none; position: fixed; bottom: 0; left: 0; right: 0; height: var(--bottom-nav-h); background: rgba(22,27,36,0.97); backdrop-filter: blur(12px); border-top: 1px solid var(--border); z-index: 190; padding: 0 8px; align-items: center; justify-content: space-around; }
@media (max-width: 768px) { .bottom-nav { display: flex; } }
.bottom-nav-item { display: flex; flex-direction: column; align-items: center; gap: 3px; background: none; border: none; color: var(--muted); cursor: pointer; padding: 6px 14px; border-radius: 10px; font-size: 0.62rem; font-family: 'Inter', sans-serif; font-weight: 500; transition: color 0.15s; flex: 1; }
.bottom-nav-item.active { color: var(--accent-light); }
.bottom-nav-item svg, .bottom-nav-item .bni { font-size: 1.3rem; line-height: 1; }

/* ── Ticker ──────────────────────────────────────────────────────── */
.ticker { background: var(--surface); border-bottom: 1px solid var(--border); overflow: hidden; height: 32px; display: flex; align-items: center; }
.ticker-track { display: flex; gap: 52px; animation: tick 32s linear infinite; white-space: nowrap; }
.ticker-track:hover { animation-play-state: paused; }
@keyframes tick { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
.ticker-item { display: flex; align-items: center; gap: 7px; font-size: 0.74rem; color: var(--muted); }
.ticker-item b { color: var(--text); }
.tick-badge { background: var(--green); color: #fff; font-size: 0.63rem; font-weight: 700; padding: 1px 5px; border-radius: 3px; }

/* ── Buttons ─────────────────────────────────────────────────────── */
.btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; font-family: 'Inter', sans-serif; font-weight: 600; border-radius: 9px; cursor: pointer; transition: all 0.15s; border: none; font-size: 0.88rem; line-height: 1; white-space: nowrap; }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-primary { background: var(--accent); color: #fff; padding: 11px 22px; }
.btn-primary:hover:not(:disabled) { background: var(--accent-light); box-shadow: 0 0 22px var(--accent-glow); transform: translateY(-1px); }
.btn-secondary { background: var(--surface2); color: var(--text); padding: 11px 22px; border: 1px solid var(--border); }
.btn-secondary:hover:not(:disabled) { border-color: var(--accent); color: var(--accent-light); }
.btn-ghost { background: none; color: var(--muted); padding: 9px 16px; border: 1px solid var(--border); }
.btn-ghost:hover { color: var(--text); border-color: var(--muted); }
.btn-green { background: var(--green); color: #fff; padding: 9px 18px; }
.btn-green:hover { background: #16A34A; }
.btn-red { background: var(--red); color: #fff; padding: 9px 18px; }
.btn-red:hover { background: #DC2626; }
.btn-sm { padding: 7px 13px; font-size: 0.78rem; border-radius: 7px; }
.btn-lg { padding: 14px 30px; font-size: 1rem; border-radius: 11px; }
.btn-full { width: 100%; }
.btn-icon { padding: 8px; border-radius: 8px; }

/* ── Toast ───────────────────────────────────────────────────────── */
.toast-wrap { position: fixed; bottom: 80px; right: 20px; z-index: 999; display: flex; flex-direction: column; gap: 8px; pointer-events: none; }
@media (min-width: 769px) { .toast-wrap { bottom: 24px; } }
.toast { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 12px 16px; font-size: 0.83rem; color: var(--text); display: flex; align-items: center; gap: 10px; min-width: 220px; max-width: 320px; box-shadow: 0 8px 24px rgba(0,0,0,0.4); animation: toast-in 0.25s ease; pointer-events: all; }
.toast.success { border-left: 3px solid var(--green); }
.toast.error { border-left: 3px solid var(--red); }
.toast.info { border-left: 3px solid var(--accent); }
@keyframes toast-in { from { opacity:0; transform: translateY(10px); } to { opacity:1; transform: translateY(0); } }

/* ── Hero ────────────────────────────────────────────────────────── */
.hero { padding: 90px 0 72px; position: relative; overflow: hidden; }
.hero::before { content:''; position:absolute; top:-160px; left:50%; transform:translateX(-50%); width:900px; height:900px; background:radial-gradient(circle, rgba(91,79,232,0.13) 0%, transparent 68%); pointer-events:none; border-radius:50%; }
.hero-pill { display:inline-flex; align-items:center; gap:6px; background:var(--surface2); border:1px solid var(--border); padding:4px 12px; border-radius:999px; font-size:0.74rem; color:var(--muted); margin-bottom:22px; }
.hero-pill span { color:var(--accent-light); font-weight:600; }
.hero h1 { font-family:var(--display); font-size:clamp(2.2rem,5.5vw,4.2rem); font-weight:700; line-height:1.1; color:#fff; max-width:680px; }
.hero h1 em { color:var(--accent); font-style:normal; }
.hero-sub { color:var(--muted); font-size:1.05rem; max-width:500px; margin-top:18px; line-height:1.7; }
.hero-actions { display:flex; gap:12px; margin-top:32px; flex-wrap:wrap; }
.hero-stats { display:flex; gap:36px; margin-top:52px; padding-top:32px; border-top:1px solid var(--border); flex-wrap:wrap; }
.hero-stat strong { font-family:var(--display); font-size:1.8rem; font-weight:700; color:#fff; display:block; }
.hero-stat label { font-size:0.73rem; color:var(--muted); text-transform:uppercase; letter-spacing:.08em; margin-top:3px; display:block; }
@media (max-width:600px) { .hero { padding:56px 0 44px; } .hero-stats { gap:20px; } }

/* ── Section ─────────────────────────────────────────────────────── */
.section { padding:60px 0; }
.sec-eyebrow { font-size:0.71rem; font-weight:700; text-transform:uppercase; letter-spacing:.1em; color:var(--accent); margin-bottom:8px; }
.sec-title { font-family:var(--display); font-size:1.85rem; font-weight:700; color:#fff; }
.sec-sub { color:var(--muted); margin-top:6px; font-size:0.93rem; }

/* ── Category grid ───────────────────────────────────────────────── */
.cat-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(100px,1fr)); gap:10px; }
.cat-card { background:var(--surface); border:1px solid var(--border); border-radius:12px; padding:18px 10px; text-align:center; cursor:pointer; transition:all .15s; }
.cat-card:hover,.cat-card.sel { border-color:var(--accent); background:var(--surface2); box-shadow:0 0 0 1px var(--accent); }
.cat-icon { font-size:1.5rem; margin-bottom:7px; }
.cat-name { font-size:0.76rem; font-weight:500; }

/* ── Listing card ────────────────────────────────────────────────── */
.listings-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(295px,1fr)); gap:18px; }
@media (max-width:640px) { .listings-grid { grid-template-columns:1fr; } }
.lcard { background:var(--surface); border:1px solid var(--border); border-radius:14px; overflow:hidden; cursor:pointer; transition:all .18s; position:relative; }
.lcard:hover { border-color:var(--accent); transform:translateY(-3px); box-shadow:0 14px 36px rgba(0,0,0,.45); }
.lcard-img { height:148px; display:flex; align-items:center; justify-content:center; font-size:3.2rem; position:relative; background:linear-gradient(135deg,var(--surface2),var(--bg)); }
.lcard-save { position:absolute; top:10px; right:10px; background:rgba(13,17,23,.7); border:1px solid var(--border); border-radius:7px; padding:5px 8px; cursor:pointer; font-size:.85rem; transition:all .15s; color:var(--muted); }
.lcard-save:hover,.lcard-save.saved { color:#F59E0B; border-color:#F59E0B; background:rgba(245,158,11,.1); }
.lcard-body { padding:15px; }
.lcard-tags { display:flex; gap:5px; margin-bottom:9px; flex-wrap:wrap; }
.tag { font-size:0.69rem; font-weight:600; padding:2px 7px; border-radius:4px; }
.tag-accent { background:var(--accent-dim); color:var(--accent-light); border:1px solid rgba(91,79,232,.3); }
.tag-green { background:var(--green-dim); color:var(--green); border:1px solid rgba(34,197,94,.25); }
.tag-muted { background:var(--surface2); color:var(--muted); border:1px solid var(--border); }
.lcard-title { font-family:var(--display); font-size:.97rem; font-weight:600; color:#fff; margin-bottom:5px; }
.lcard-desc { font-size:.79rem; color:var(--muted); line-height:1.5; margin-bottom:13px; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
.lcard-metrics { display:grid; grid-template-columns:1fr 1fr; gap:7px; border-top:1px solid var(--border); padding-top:12px; }
.lm-item label { font-size:.66rem; color:var(--muted); display:block; margin-bottom:1px; }
.lm-item strong { font-family:var(--display); font-size:.93rem; font-weight:600; }
.lm-item.price strong { color:var(--accent-light); font-size:1.02rem; }
.lm-item.growth strong { color:var(--green); }
.verified-badge { display:inline-flex; align-items:center; gap:4px; font-size:.67rem; font-weight:700; color:var(--green); background:var(--green-dim); border:1px solid rgba(34,197,94,.25); padding:2px 7px; border-radius:4px; }

/* ── How it works ────────────────────────────────────────────────── */
.hiw-grid { display:grid; grid-template-columns:1fr 1fr; gap:44px; }
@media (max-width:640px) { .hiw-grid { grid-template-columns:1fr; gap:32px; } }
.hiw-side h3 { font-family:var(--display); font-size:1.05rem; font-weight:600; color:#fff; margin-bottom:18px; padding-bottom:12px; border-bottom:1px solid var(--border); }
.hiw-step { display:flex; gap:13px; margin-bottom:18px; }
.hiw-num { width:26px; height:26px; border-radius:7px; background:var(--accent); color:#fff; font-family:var(--display); font-size:0.73rem; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; margin-top:1px; }
.hiw-text h4 { font-size:.87rem; font-weight:600; color:var(--text); }
.hiw-text p { font-size:.78rem; color:var(--muted); margin-top:2px; }

/* ── Pricing cards ───────────────────────────────────────────────── */
.pricing-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(210px,1fr)); gap:14px; max-width:920px; margin:0 auto; }
.price-card { background:var(--surface2); border:1px solid var(--border); border-radius:13px; padding:22px; transition:border-color .15s; }
.price-card.featured { background:var(--accent-dim); border-color:var(--accent); }
.price-card .pc-label { font-size:.73rem; color:var(--muted); margin-bottom:5px; }
.price-card .pc-price { font-family:var(--display); font-size:1.9rem; font-weight:700; color:#fff; margin-bottom:7px; }
.price-card.featured .pc-price { color:var(--accent-light); }
.price-card .pc-desc { font-size:.81rem; color:var(--muted); line-height:1.5; }

/* ── Filters ─────────────────────────────────────────────────────── */
.filters-bar { display:flex; gap:8px; flex-wrap:wrap; margin-bottom:24px; align-items:center; }
.search-input { flex:1; min-width:200px; background:var(--surface); border:1px solid var(--border); color:var(--text); border-radius:8px; padding:9px 13px; font-family:'Inter',sans-serif; font-size:.88rem; }
.search-input::placeholder { color:var(--muted); }
.search-input:focus { outline:none; border-color:var(--accent); box-shadow:0 0 0 3px var(--accent-glow); }
.f-select { background:var(--surface); border:1px solid var(--border); color:var(--text); border-radius:8px; padding:9px 13px; font-family:'Inter',sans-serif; font-size:.82rem; cursor:pointer; }
.f-select:focus { outline:none; border-color:var(--accent); }
.f-toggle { display:flex; align-items:center; gap:6px; font-size:.82rem; color:var(--muted); cursor:pointer; white-space:nowrap; padding:9px 0; }
.f-toggle input { accent-color:var(--accent); }
.results-count { font-size:.82rem; color:var(--muted); margin-bottom:18px; }

/* ── Detail page ─────────────────────────────────────────────────── */
.detail-back { display:inline-flex; align-items:center; gap:6px; font-size:.82rem; color:var(--muted); cursor:pointer; margin-bottom:20px; padding:6px 0; }
.detail-back:hover { color:var(--text); }
.detail-head { display:flex; align-items:flex-start; gap:18px; margin-bottom:28px; }
.detail-logo-box { width:68px; height:68px; background:var(--surface2); border-radius:14px; display:flex; align-items:center; justify-content:center; font-size:2rem; flex-shrink:0; border:1px solid var(--border); }
.detail-title { font-family:var(--display); font-size:1.7rem; font-weight:700; color:#fff; line-height:1.2; }
.detail-url { color:var(--muted); font-size:.84rem; margin-top:4px; }
.detail-badges { display:flex; gap:7px; margin-top:9px; flex-wrap:wrap; }
.detail-layout { display:grid; grid-template-columns:1fr 310px; gap:24px; align-items:start; }
@media (max-width:768px) { .detail-layout { grid-template-columns:1fr; } }
.detail-card { background:var(--surface); border:1px solid var(--border); border-radius:13px; padding:20px; margin-bottom:18px; }
.detail-card h3 { font-family:var(--display); font-size:.97rem; font-weight:600; color:#fff; margin-bottom:14px; padding-bottom:11px; border-bottom:1px solid var(--border); }
.metrics-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:12px; }
@media (max-width:480px) { .metrics-grid { grid-template-columns:repeat(2,1fr); } }
.metric-box { background:var(--bg); border:1px solid var(--border); border-radius:9px; padding:13px; text-align:center; }
.metric-box .val { font-family:var(--display); font-size:1.3rem; font-weight:700; }
.metric-box .lbl { font-size:.68rem; color:var(--muted); margin-top:3px; text-transform:uppercase; letter-spacing:.05em; }
.bar-chart-wrap { overflow:hidden; }
.bar-chart { display:flex; align-items:flex-end; gap:5px; height:80px; }
.bar { flex:1; border-radius:3px 3px 0 0; min-width:8px; transition:opacity .15s; }
.chart-labels { display:flex; gap:5px; margin-top:5px; }
.chart-label { flex:1; font-size:.62rem; color:var(--muted); text-align:center; min-width:8px; }
.assets-list { display:flex; flex-direction:column; gap:7px; }
.asset-row { display:flex; align-items:center; gap:8px; font-size:.84rem; }
.asset-row::before { content:'✓'; color:var(--green); font-weight:700; flex-shrink:0; }
.tech-tags { display:flex; flex-wrap:wrap; gap:6px; }
.sidebar-card { background:var(--surface); border:1px solid var(--border); border-radius:13px; padding:20px; margin-bottom:14px; }
.price-big { font-family:var(--display); font-size:1.9rem; font-weight:700; color:var(--accent-light); }
.price-note { font-size:.79rem; color:var(--muted); margin-top:3px; margin-bottom:18px; }
.offer-field { width:100%; background:var(--bg); border:1px solid var(--border); color:var(--text); border-radius:8px; padding:10px 13px; font-family:var(--display); font-size:1.05rem; margin-bottom:9px; }
.offer-field:focus { outline:none; border-color:var(--accent); }
.seller-row { display:flex; align-items:center; gap:11px; }
.s-avatar { width:38px; height:38px; border-radius:50%; background:var(--accent); display:flex; align-items:center; justify-content:center; font-weight:700; font-size:.85rem; flex-shrink:0; }
.escrow-note { background:rgba(91,79,232,.08); border:1px solid rgba(91,79,232,.2); border-radius:9px; padding:12px 14px; font-size:.8rem; color:var(--muted); line-height:1.6; }

/* ── Forms ───────────────────────────────────────────────────────── */
.form-page { min-height:calc(100vh - var(--nav-h)); display:flex; align-items:center; justify-content:center; padding:32px 16px 80px; }
.form-card { background:var(--surface); border:1px solid var(--border); border-radius:16px; padding:32px; width:100%; max-width:420px; }
.form-card h2 { font-family:var(--display); font-size:1.5rem; font-weight:700; color:#fff; margin-bottom:5px; }
.form-card .sub { color:var(--muted); font-size:.86rem; margin-bottom:24px; }
.form-row { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.fg { margin-bottom:14px; }
.fg label { display:block; font-size:.79rem; font-weight:500; color:var(--muted); margin-bottom:5px; }
.fi { width:100%; background:var(--bg); border:1px solid var(--border); color:var(--text); border-radius:8px; padding:10px 13px; font-family:'Inter',sans-serif; font-size:.88rem; }
.fi:focus { outline:none; border-color:var(--accent); box-shadow:0 0 0 3px var(--accent-glow); }
.fi::placeholder { color:var(--muted); }
.ta { width:100%; background:var(--bg); border:1px solid var(--border); color:var(--text); border-radius:8px; padding:10px 13px; font-family:'Inter',sans-serif; font-size:.88rem; resize:vertical; min-height:90px; }
.ta:focus { outline:none; border-color:var(--accent); box-shadow:0 0 0 3px var(--accent-glow); }
.divider { display:flex; align-items:center; gap:10px; margin:18px 0; color:var(--muted); font-size:.76rem; }
.divider::before,.divider::after { content:''; flex:1; height:1px; background:var(--border); }
.oauth-btn { width:100%; display:flex; align-items:center; justify-content:center; gap:8px; background:var(--surface2); border:1px solid var(--border); color:var(--text); border-radius:8px; padding:10px; cursor:pointer; font-family:'Inter',sans-serif; font-size:.86rem; margin-bottom:9px; transition:border-color .15s; }
.oauth-btn:hover { border-color:var(--muted); }
.form-foot { text-align:center; margin-top:18px; font-size:.81rem; color:var(--muted); }
.form-foot a { color:var(--accent-light); cursor:pointer; }
.chk { display:flex; align-items:flex-start; gap:7px; margin-bottom:9px; font-size:.79rem; color:var(--muted); cursor:pointer; }
.chk input { margin-top:2px; accent-color:var(--accent); }
.form-error { font-size:.78rem; color:var(--red); margin-top:4px; }

/* ── Wizard ──────────────────────────────────────────────────────── */
.wizard-wrap { max-width:660px; margin:0 auto; padding:28px 16px 100px; }
.wiz-prog-track { background:var(--border); height:3px; border-radius:2px; margin-bottom:6px; overflow:hidden; }
.wiz-prog-fill { background:var(--accent); height:100%; border-radius:2px; transition:width .3s; }
.wiz-step-info { font-size:.74rem; color:var(--muted); margin-bottom:18px; }
.wiz-title { font-family:var(--display); font-size:1.45rem; font-weight:700; color:#fff; }
.wiz-sub { color:var(--muted); font-size:.85rem; margin-top:4px; margin-bottom:24px; }
.wiz-card { background:var(--surface); border:1px solid var(--border); border-radius:14px; padding:24px; margin-bottom:18px; }
.wiz-nav { display:flex; justify-content:space-between; gap:10px; }
.opt-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(120px,1fr)); gap:9px; }
.opt-card { background:var(--bg); border:2px solid var(--border); border-radius:10px; padding:16px 10px; text-align:center; cursor:pointer; transition:all .15s; }
.opt-card:hover { border-color:var(--accent-light); }
.opt-card.sel { border-color:var(--accent); background:var(--accent-dim); }
.opt-icon { font-size:1.5rem; margin-bottom:7px; }
.opt-label { font-size:.78rem; font-weight:500; }
.ms-wrap { display:flex; flex-wrap:wrap; gap:7px; margin-top:8px; }
.ms-chip { padding:6px 13px; border-radius:7px; border:1px solid var(--border); font-size:.81rem; cursor:pointer; transition:all .13s; background:var(--bg); }
.ms-chip:hover { border-color:var(--accent-light); }
.ms-chip.sel { background:var(--accent-dim); border-color:var(--accent); color:var(--accent-light); }
.assets-check-grid { display:grid; grid-template-columns:1fr 1fr; gap:9px; }
@media (max-width:400px) { .assets-check-grid { grid-template-columns:1fr; } }
.asset-chk { display:flex; align-items:center; gap:9px; background:var(--bg); border:1px solid var(--border); border-radius:8px; padding:11px 13px; cursor:pointer; font-size:.84rem; }
.asset-chk input { accent-color:var(--accent); }
.int-card { display:flex; align-items:center; justify-content:space-between; background:var(--bg); border:1px solid var(--border); border-radius:9px; padding:13px 15px; margin-bottom:9px; }
.int-card .left { display:flex; align-items:center; gap:11px; font-size:.88rem; }
.val-tip { background:rgba(34,197,94,.07); border:1px solid rgba(34,197,94,.2); border-radius:8px; padding:11px 13px; font-size:.8rem; color:var(--muted); margin-top:12px; line-height:1.6; }
.review-tbl { width:100%; }
.review-tbl tr { border-bottom:1px solid var(--border); }
.review-tbl td { padding:9px 0; font-size:.84rem; }
.review-tbl td:first-child { color:var(--muted); width:42%; }
.review-tbl td:last-child { color:var(--text); font-weight:500; }

/* ── Dashboard ───────────────────────────────────────────────────── */
.dash-wrap { display:grid; grid-template-columns:220px 1fr; min-height:calc(100vh - var(--nav-h)); }
@media (max-width:768px) { .dash-wrap { grid-template-columns:1fr; } }
.dash-side { background:var(--surface); border-right:1px solid var(--border); display:flex; flex-direction:column; }
@media (max-width:768px) { .dash-side { display:none; } }
.dash-side-user { padding:16px 18px 14px; border-bottom:1px solid var(--border); display:flex; align-items:center; gap:10px; }
.dash-side-avatar { width:34px; height:34px; border-radius:50%; background:var(--accent); display:flex; align-items:center; justify-content:center; font-weight:700; font-size:.82rem; flex-shrink:0; }
.dash-side-name { font-size:.84rem; font-weight:600; }
.dash-side-role { font-size:.7rem; color:var(--muted); }
.dash-nav { padding:10px 0; flex:1; }
.dash-nav-item { display:flex; align-items:center; gap:10px; padding:10px 18px; cursor:pointer; font-size:.855rem; color:var(--muted); transition:all .12s; border-right:2px solid transparent; }
.dash-nav-item:hover { color:var(--text); background:var(--surface2); }
.dash-nav-item.active { color:var(--accent-light); background:var(--accent-dim); border-right-color:var(--accent); }
.dash-side-bottom { padding:16px 18px; border-top:1px solid var(--border); }
.dash-main { padding:24px; background:var(--bg); overflow-x:hidden; }
@media (max-width:768px) { .dash-main { padding:16px; } }
.dash-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:22px; flex-wrap:wrap; gap:12px; }
.dash-page-title { font-family:var(--display); font-size:1.4rem; font-weight:700; color:#fff; }
.dash-stats-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:12px; margin-bottom:22px; }
@media (min-width:640px) { .dash-stats-grid { grid-template-columns:repeat(4,1fr); } }
.ds-card { background:var(--surface); border:1px solid var(--border); border-radius:12px; padding:16px; }
.ds-label { font-size:.7rem; color:var(--muted); text-transform:uppercase; letter-spacing:.07em; margin-bottom:5px; }
.ds-val { font-family:var(--display); font-size:1.55rem; font-weight:700; color:#fff; }
.ds-delta { font-size:.73rem; color:var(--green); margin-top:2px; }
.ds-delta.neg { color:var(--red); }
/* Mobile-first table: scrollable wrapper */
.table-scroll { overflow-x:auto; -webkit-overflow-scrolling:touch; border-radius:12px; border:1px solid var(--border); }
.data-table { width:100%; border-collapse:collapse; min-width:560px; background:var(--surface); }
.data-table th { font-size:.7rem; text-transform:uppercase; letter-spacing:.07em; color:var(--muted); padding:11px 14px; text-align:left; border-bottom:1px solid var(--border); white-space:nowrap; }
.data-table td { padding:12px 14px; font-size:.84rem; border-bottom:1px solid var(--border); color:var(--text); white-space:nowrap; }
.data-table tr:last-child td { border-bottom:none; }
.data-table tr:hover td { background:rgba(255,255,255,.02); }
.status-pill { display:inline-block; font-size:.68rem; font-weight:600; padding:3px 8px; border-radius:4px; }
.sp-live { background:var(--green-dim); color:var(--green); }
.sp-pending { background:var(--gold-dim); color:var(--gold); }
.sp-rejected { background:var(--red-dim); color:var(--red); }
.sp-draft { background:var(--surface2); color:var(--muted); }
.action-btns { display:flex; gap:6px; }
.section-card { background:var(--surface); border:1px solid var(--border); border-radius:12px; padding:18px; margin-bottom:18px; }
.section-card-title { font-family:var(--display); font-size:.97rem; font-weight:600; color:#fff; margin-bottom:14px; }

/* ── Saved / Wishlist ────────────────────────────────────────────── */
.saved-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(260px,1fr)); gap:14px; }
@media (max-width:480px) { .saved-grid { grid-template-columns:1fr; } }
.saved-mini { background:var(--surface2); border:1px solid var(--border); border-radius:10px; padding:14px; display:flex; gap:12px; align-items:center; cursor:pointer; transition:border-color .15s; }
.saved-mini:hover { border-color:var(--accent); }
.saved-emoji { font-size:1.6rem; flex-shrink:0; }

/* ── Valuation calculator ────────────────────────────────────────── */
.calc-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
@media (max-width:480px) { .calc-grid { grid-template-columns:1fr; } }
.calc-result { background:var(--accent-dim); border:1px solid var(--accent); border-radius:12px; padding:20px; text-align:center; margin-top:16px; }
.calc-result .val { font-family:var(--display); font-size:2rem; font-weight:700; color:var(--accent-light); }
.calc-result .range { font-size:.82rem; color:var(--muted); margin-top:5px; }
.calc-result .breakdown { display:flex; justify-content:center; gap:24px; margin-top:14px; font-size:.8rem; }
.calc-result .breakdown div { color:var(--muted); }
.calc-result .breakdown strong { display:block; color:var(--text); font-family:var(--display); font-size:1rem; }

/* ── Notifications ───────────────────────────────────────────────── */
.notif-panel { position:absolute; right:0; top:calc(100% + 8px); width:320px; background:var(--surface); border:1px solid var(--border); border-radius:12px; box-shadow:0 12px 40px rgba(0,0,0,.5); z-index:300; overflow:hidden; }
@media (max-width:400px) { .notif-panel { width:280px; right:-60px; } }
.notif-header { padding:12px 16px; border-bottom:1px solid var(--border); display:flex; justify-content:space-between; align-items:center; }
.notif-header h4 { font-family:var(--display); font-size:.92rem; font-weight:600; }
.notif-item { padding:12px 16px; border-bottom:1px solid var(--border); display:flex; gap:10px; align-items:flex-start; transition:background .12s; cursor:pointer; }
.notif-item:hover { background:var(--surface2); }
.notif-item.unread { background:var(--accent-dim); }
.notif-dot { width:7px; height:7px; border-radius:50%; background:var(--accent); flex-shrink:0; margin-top:5px; }
.notif-text { font-size:.8rem; color:var(--text); line-height:1.4; }
.notif-time { font-size:.7rem; color:var(--muted); margin-top:2px; }
.notif-empty { padding:28px; text-align:center; color:var(--muted); font-size:.84rem; }

/* ── Messages ────────────────────────────────────────────────────── */
.msgs-wrap { display:grid; grid-template-columns:275px 1fr; height:calc(100vh - var(--nav-h) - var(--bottom-nav-h)); }
@media (max-width:768px) { .msgs-wrap { grid-template-columns:1fr; height:calc(100vh - var(--nav-h) - var(--bottom-nav-h)); } }
.thread-list { background:var(--surface); border-right:1px solid var(--border); overflow-y:auto; display:flex; flex-direction:column; }
@media (max-width:768px) { .thread-list { display:none; } }
.tl-header { padding:14px 16px; border-bottom:1px solid var(--border); font-family:var(--display); font-size:.92rem; font-weight:600; flex-shrink:0; }
.thread-item { display:flex; gap:10px; padding:13px 15px; cursor:pointer; border-bottom:1px solid var(--border); transition:background .12s; }
.thread-item:hover,.thread-item.active { background:var(--surface2); }
.ti-avatar { width:34px; height:34px; border-radius:50%; background:var(--accent); display:flex; align-items:center; justify-content:center; font-size:.78rem; font-weight:700; flex-shrink:0; }
.ti-name { font-size:.84rem; font-weight:600; }
.ti-product { font-size:.7rem; color:var(--accent-light); margin:1px 0; }
.ti-preview { font-size:.74rem; color:var(--muted); overflow:hidden; white-space:nowrap; text-overflow:ellipsis; }
.ti-time { font-size:.68rem; color:var(--muted); white-space:nowrap; }
.chat-wrap { display:flex; flex-direction:column; background:var(--bg); }
.chat-top { padding:13px 18px; border-bottom:1px solid var(--border); background:var(--surface); display:flex; align-items:center; gap:11px; flex-shrink:0; }
.chat-msgs { flex:1; overflow-y:auto; padding:18px; display:flex; flex-direction:column; gap:11px; }
.bubble-wrap { max-width:68%; }
.bubble-wrap.me { align-self:flex-end; }
.bubble-wrap.them { align-self:flex-start; }
.bubble { padding:9px 13px; border-radius:11px; font-size:.86rem; line-height:1.5; }
.bubble-wrap.me .bubble { background:var(--accent); color:#fff; border-radius:11px 11px 2px 11px; }
.bubble-wrap.them .bubble { background:var(--surface); border:1px solid var(--border); border-radius:11px 11px 11px 2px; }
.bubble-time { font-size:.67rem; color:var(--muted); margin-top:3px; text-align:right; }
.chat-bottom { padding:12px 16px; border-top:1px solid var(--border); display:flex; gap:8px; flex-shrink:0; }
.chat-fi { flex:1; background:var(--surface); border:1px solid var(--border); color:var(--text); border-radius:9px; padding:10px 13px; font-family:'Inter',sans-serif; font-size:.88rem; }
.chat-fi:focus { outline:none; border-color:var(--accent); }

/* ── Offer history in detail ─────────────────────────────────────── */
.offer-history { display:flex; flex-direction:column; gap:8px; }
.oh-item { background:var(--bg); border:1px solid var(--border); border-radius:8px; padding:11px 13px; display:flex; justify-content:space-between; align-items:center; font-size:.83rem; }
.oh-amt { font-family:var(--display); font-weight:600; font-size:.95rem; color:var(--accent-light); }

/* ── Success / empty ─────────────────────────────────────────────── */
.success-card { text-align:center; padding:48px 20px; }
.success-card .icon { font-size:2.8rem; margin-bottom:14px; }
.success-card h2 { font-family:var(--display); font-size:1.5rem; color:#fff; margin-bottom:7px; }
.success-card p { color:var(--muted); font-size:.88rem; margin-bottom:20px; }
.empty-state { text-align:center; padding:48px 20px; color:var(--muted); }
.empty-state .icon { font-size:2.2rem; margin-bottom:10px; }
.empty-state p { font-size:.86rem; }

/* ── Footer ──────────────────────────────────────────────────────── */
.footer { background:var(--surface); border-top:1px solid var(--border); padding:40px 0 24px; margin-top:auto; }
.footer-grid { display:grid; grid-template-columns:2fr 1fr 1fr 1fr; gap:28px; margin-bottom:28px; }
@media (max-width:640px) { .footer-grid { grid-template-columns:1fr 1fr; } }
@media (max-width:400px) { .footer-grid { grid-template-columns:1fr; } }
.footer-logo { font-family:var(--display); font-size:1.05rem; font-weight:700; color:#fff; margin-bottom:9px; }
.footer-logo span { color:var(--accent); }
.footer-desc { font-size:.8rem; color:var(--muted); line-height:1.6; margin-bottom:16px; }
.footer-col h4 { font-size:.73rem; font-weight:600; text-transform:uppercase; letter-spacing:.08em; color:var(--muted); margin-bottom:11px; }
.footer-col a { display:block; font-size:.81rem; color:var(--muted); margin-bottom:7px; cursor:pointer; transition:color .12s; }
.footer-col a:hover { color:var(--text); }
.footer-bottom { border-top:1px solid var(--border); padding-top:18px; display:flex; justify-content:space-between; font-size:.76rem; color:var(--muted); flex-wrap:wrap; gap:6px; align-items:center; }
.social-links { display:flex; gap:10px; align-items:center; }
.social-link { display:inline-flex; align-items:center; justify-content:center; width:34px; height:34px; border-radius:8px; background:var(--surface2); border:1px solid var(--border); color:var(--muted); cursor:pointer; transition:all .18s; text-decoration:none; }
.social-link:hover { color:#fff; border-color:var(--accent); background:var(--accent-dim); transform:translateY(-2px); box-shadow:0 4px 14px var(--accent-glow); }
.social-link.insta:hover { color:#E1306C; border-color:#E1306C; background:rgba(225,48,108,0.12); box-shadow:0 4px 14px rgba(225,48,108,0.25); }
.social-link.threads:hover { color:#fff; border-color:#fff; background:rgba(255,255,255,0.08); }
.social-link.x-tw:hover { color:#fff; border-color:#fff; background:rgba(255,255,255,0.08); }
.social-link.tiktok:hover { color:#69C9D0; border-color:#69C9D0; background:rgba(105,201,208,0.12); box-shadow:0 4px 14px rgba(105,201,208,0.2); }
`;

/* ─── Content Page Data ─────────────────────────────────────────────── */

const PAGE_CONTENTS = {
  "sell-landing": {
    title: "Sell Your Software on SoftSell",
    content: `<p>The fastest way to exit your SaaS, AI tool, app, plugin, or digital product. List free in minutes. Pay only when you sell.</p>
<h3>Why sellers choose SoftSell:</h3>
<ul><li>Free to list — no upfront fees</li><li>4% success fee, only on completed sales</li><li>Verified revenue and traffic badges build buyer trust</li><li>Direct messaging with serious, vetted buyers</li><li>Guided transfer checklist so nothing gets missed</li><li>Escrow-protected transactions</li></ul>
<h3>Who lists on SoftSell:</h3>
<p>Indie developers, bootstrapped founders, solo operators, agencies, and no-code builders selling products at any size — from a $500 Chrome extension to a $500,000 SaaS.</p>
<h3>What you can sell:</h3>
<p>SaaS products · AI tools · Chrome extensions · WordPress plugins · Shopify apps · Mobile apps · APIs · No-code products · Digital products · Internal tools · Automation systems</p>
<h3>How it works:</h3>
<ol><li>Create your free account</li><li>Complete the listing wizard (takes under 10 minutes)</li><li>Connect Stripe or analytics for verified badges</li><li>Go live — buyers can find and message you immediately</li><li>Receive offers and negotiate directly</li><li>Close the deal with escrow protection and a guided transfer checklist</li></ol>
<h3>Listing tiers:</h3>
<table><thead><tr><th>Plan</th><th>Price</th><th>What's included</th></tr></thead><tbody><tr><td><strong>Free Listing</strong></td><td>$0</td><td>Unlimited listings, offer system, messaging</td></tr><tr><td><strong>Verified Badge</strong></td><td>$49</td><td>Revenue + traffic verification, ✓ badge</td></tr><tr><td><strong>Featured</strong></td><td>$99</td><td>Homepage placement, search boost, newsletter</td></tr><tr><td><strong>Success Fee</strong></td><td>4%</td><td>Charged on completed sale only</td></tr></tbody></table>
<p><em>Ready to list? Create your free account and publish in minutes.</em></p>`
  },
  "featured-listings": {
    title: "Get Your Listing Seen First",
    content: `<p>Featured placement puts your software in front of the most active buyers on SoftSell. Listings featured on the homepage and at the top of search results receive significantly more views, inquiries, and offers.</p>
<h3>Featured placement includes:</h3>
<ul><li>Homepage spotlight (above the fold)</li><li>Top position in category search results</li><li>Bold "Featured" badge on your listing card</li><li>Inclusion in the SoftSell weekly buyer newsletter (8,000+ subscribers)</li><li>Priority in new listing email alerts sent to active buyers</li></ul>
<h3>Pricing:</h3>
<table><thead><tr><th>Package</th><th>Price</th><th>Duration</th></tr></thead><tbody><tr><td><strong>Featured Listing</strong></td><td>$99</td><td>30 days</td></tr><tr><td><strong>Homepage Spotlight</strong></td><td>$149</td><td>7 days</td></tr><tr><td><strong>Newsletter Feature</strong></td><td>$79</td><td>One issue</td></tr></tbody></table>
<p>Featured spots are limited. Only a small number of listings are featured at any time to maintain quality and buyer attention.</p>
<p><em>Featured placement is available after your listing is approved and live.</em></p>`
  },
  "how-it-works": {
    title: "How It Works",
    content: `<p><strong>SoftSell makes buying and selling software businesses simple, safe, and transparent.</strong></p>
<hr/>
<h2>For Sellers</h2>
<h3>Step 1 — Create Your Account</h3><p>Sign up free with your email or GitHub. No credit card required. Your account gives you access to the full listing wizard, buyer messaging, offer management, and your seller dashboard.</p>
<h3>Step 2 — List Your Software</h3><p>Complete the guided listing wizard. Add your product details, screenshots, revenue metrics, tech stack, and asking price. The wizard takes under 10 minutes. You can save a draft and return at any time.</p>
<h3>Step 3 — Get Verified</h3><p>Connect Stripe, Paddle, or Lemon Squeezy to automatically verify your revenue. Connect Google Analytics or Plausible to verify traffic. Verified listings earn a ✓ badge and receive significantly more buyer interest.</p>
<h3>Step 4 — Go Live</h3><p>Submit your listing for review. Our team reviews all listings within 24 hours. Once approved, your listing appears in the marketplace immediately.</p>
<h3>Step 5 — Receive Offers</h3><p>Buyers browse your listing, view your metrics, and submit offers directly through the platform. You can accept, decline, or counter any offer from your dashboard.</p>
<h3>Step 6 — Negotiate</h3><p>Use the built-in messaging system to communicate with buyers. Share files, answer questions, and negotiate terms — all in one place.</p>
<h3>Step 7 — Sign an LOI</h3><p>When you reach agreement with a buyer, a Letter of Intent (LOI) is generated automatically. This outlines the agreed price, timeline, and terms of the sale.</p>
<h3>Step 8 — Due Diligence</h3><p>Give the buyer access to the secure due diligence room. You control exactly what each buyer can see.</p>
<h3>Step 9 — Escrow and Transfer</h3><p>The buyer deposits funds into escrow. You work through the transfer checklist: domain, source code, database, hosting, payment processor, social accounts. When the buyer confirms, escrow releases funds to you.</p>
<h3>Step 10 — Done</h3><p>SoftSell collects its 4% success fee. You receive the remainder. The listing is marked as sold.</p>
<hr/>
<h2>For Buyers</h2>
<h3>Step 1 — Create a Buyer Account</h3><p>Sign up free. Tell us whether you're an individual, company, or agency.</p>
<h3>Step 2 — Browse Listings</h3><p>Filter by category, revenue, price, tech stack, and verification status.</p>
<h3>Step 3 — Review Metrics</h3><p>Every listing shows monthly revenue, profit, growth rate, customer count, and churn rate. Verified listings display badges confirming data has been independently confirmed.</p>
<h3>Step 4 — Make an Offer</h3><p>Submit your offer amount along with a message introducing yourself and your plans.</p>
<h3>Step 5 — Escrow and Transfer</h3><p>Deposit the agreed amount into escrow. Once you confirm successful receipt of all assets, funds are released to the seller.</p>
<hr/>
<h2>Fees</h2>
<p>SoftSell charges sellers a 4% success fee on completed transactions. There are no fees for buyers. Listing is free.</p>
<h2>Is SoftSell available internationally?</h2>
<p>Yes. Sellers and buyers from any country can use SoftSell. Transactions are conducted in USD. You are responsible for your own tax reporting obligations in your jurisdiction.</p>`
  },
  "buyer-guide": {
    title: "Buyer Guide",
    content: `<p><strong>Everything you need to know before buying a software business on SoftSell.</strong></p>
<h3>Is buying a software business right for me?</h3>
<p>Buying an existing software business gives you a head start. Instead of building from scratch, you acquire a product that already has customers, revenue, and a track record. SoftSell is used by individual developers, entrepreneurs, agencies, and investment groups. There is no minimum purchase size. You can acquire a Chrome extension for $2,000 or a SaaS business for $500,000.</p>
<h3>How to evaluate a listing</h3>
<ul>
<li><strong>Revenue and profit.</strong> Always look at both. High revenue with low profit may indicate expensive infrastructure or poor margins. Ask for a profit and loss breakdown.</li>
<li><strong>Growth trend.</strong> A business growing 10% month-over-month is very different from one that has been flat for 12 months. Ask for historical monthly revenue data.</li>
<li><strong>Churn rate.</strong> For subscription businesses, churn tells you how sticky the product is. Churn above 5% monthly is a significant risk factor.</li>
<li><strong>Customer concentration.</strong> If 80% of revenue comes from three customers, losing any one of them would dramatically affect the business.</li>
<li><strong>Tech stack.</strong> Evaluate whether you or your team can maintain and improve the codebase.</li>
<li><strong>Traffic sources.</strong> Is traffic organic, paid, or dependent on one channel? A business heavily dependent on a single traffic source is more fragile.</li>
<li><strong>Reason for selling.</strong> Understand why the seller is exiting. Burnout, new project, and cash requirement are common and legitimate.</li>
</ul>
<h3>Questions to ask every seller</h3>
<ul>
<li>Can you provide 12 months of Stripe or payment processor history?</li>
<li>What does your monthly churn look like? Do you have cohort data?</li>
<li>How many hours per week does the business require to operate?</li>
<li>Is there any staff, contractors, or ongoing service agreements?</li>
<li>Are there any outstanding legal disputes or customer complaints?</li>
<li>What happens to existing customer contracts after transfer?</li>
<li>Are there any dependencies on third-party APIs or services?</li>
<li>Can you walk me through the codebase?</li>
</ul>
<h3>Red flags to watch for</h3>
<ul>
<li>Revenue that cannot be verified through a payment processor</li>
<li>Sellers who are reluctant to provide documentation or answer direct questions</li>
<li>Unusually high revenue multiples without a clear justification</li>
<li>Traffic that comes entirely from one source</li>
<li>Very recent revenue spikes with no clear explanation</li>
</ul>
<h3>How escrow works</h3>
<p>When you reach agreement with a seller, funds are deposited into escrow before transfer begins. The seller cannot access the funds until you confirm that all agreed assets have been received. If transfer does not complete successfully, funds are returned to you.</p>
<h3>After you buy</h3>
<p>Plan for a transition period. Most sellers offer 15–30 days of support after transfer. Use this time to get familiar with the codebase, meet the key customers or partners, and take over all accounts.</p>`
  },
  "seller-guide": {
    title: "Seller Guide",
    content: `<p><strong>Everything you need to know before listing your software on SoftSell.</strong></p>
<h3>Is SoftSell right for my business?</h3>
<p>SoftSell is designed for software businesses of any size. Unlike traditional acquisition platforms that focus on businesses doing $1M+ in revenue, SoftSell is built for the full range — from a $500 side project to a $500,000 bootstrapped SaaS. There is no minimum revenue requirement to list.</p>
<h3>How to price your listing</h3>
<p>The most common valuation method for software businesses is a revenue multiple — specifically, a multiple of annual recurring revenue (ARR) or annual profit.</p>
<table><thead><tr><th>Category</th><th>Typical Multiple</th></tr></thead><tbody>
<tr><td>SaaS (subscription)</td><td>3–5× ARR</td></tr>
<tr><td>AI Tools</td><td>3–5× ARR</td></tr>
<tr><td>Mobile Apps</td><td>2–4× ARR</td></tr>
<tr><td>Chrome Extensions</td><td>2–3× ARR</td></tr>
<tr><td>WordPress Plugins</td><td>2–3× ARR</td></tr>
<tr><td>One-time purchase products</td><td>2–3× annual revenue</td></tr>
<tr><td>No-code / automation</td><td>2–3× ARR</td></tr>
</tbody></table>
<p>Factors that <strong>increase</strong> your multiple: Strong MoM growth (>10%), low churn (<2%), verified revenue and traffic, long operating history (2+ years), diversified traffic sources, clean documented codebase.</p>
<p>Factors that <strong>decrease</strong> your multiple: Declining revenue, high churn (>5%), heavy customer concentration, single traffic source dependency, technical debt, founder dependency.</p>
<h3>How to write a strong listing</h3>
<ul>
<li><strong>Be specific about revenue.</strong> "$2,100 MRR with 98% retention" beats "around $2,000 per month."</li>
<li><strong>Explain your business model clearly.</strong> Is it subscription? One-time? Usage-based?</li>
<li><strong>Describe the customer.</strong> Who uses your product? What problem does it solve?</li>
<li><strong>Be honest about why you're selling.</strong> Sellers who answer this clearly build more trust.</li>
<li><strong>Describe what's included.</strong> Source code, domain, database, documentation, social accounts, customer list, brand assets.</li>
<li><strong>Describe the time commitment.</strong> How many hours per week? Is there a team?</li>
<li><strong>Show the history.</strong> Share month-by-month revenue for the past 12 months if possible.</li>
</ul>
<h3>Verification</h3>
<ul>
<li><strong>Revenue Verification ($49)</strong> — Connect Stripe, Paddle, Lemon Squeezy, or PayPal. We display a ✓ Revenue Verified badge.</li>
<li><strong>Traffic Verification ($49)</strong> — Connect Google Analytics, Plausible, or Fathom.</li>
<li><strong>Full Verification Bundle ($79)</strong> — Both revenue and traffic verification at a reduced combined price.</li>
</ul>
<h3>Taxes</h3>
<p>You are responsible for reporting and paying any applicable taxes on the sale of your software business in your jurisdiction. SoftSell does not provide tax advice.</p>`
  },
  "blog": {
    title: "Blog",
    content: `<p><strong>SoftSell Blog — Insights on buying and selling software businesses</strong></p>
<p>Topics we cover:</p>
<ul>
<li>How to value your SaaS for sale</li>
<li>What buyers look for before making an offer</li>
<li>How to prepare your codebase for handover</li>
<li>AI tool acquisition trends</li>
<li>Chrome extension market report</li>
<li>From listing to close: a seller's timeline</li>
<li>Due diligence checklist for software buyers</li>
<li>How to write a listing that gets offers</li>
</ul>
<p><em>New articles published weekly. Subscribe to our newsletter for the latest.</em></p>`
  },
  "faq": {
    title: "Frequently Asked Questions",
    content: `<h2>General</h2>
<h3>What is SoftSell?</h3><p>SoftSell is a marketplace for buying and selling software businesses and digital software assets. Sellers list their products — SaaS, AI tools, apps, Chrome extensions, WordPress plugins, and more — and buyers browse, analyze, and make offers directly through the platform.</p>
<h3>Who is SoftSell for?</h3><p>SoftSell is for indie developers, bootstrapped founders, solo operators, agencies, no-code builders, and anyone who owns a software product and wants to sell it — or anyone who wants to buy one. We support businesses of all sizes, from a $500 Chrome extension to a $500,000 SaaS.</p>
<h3>Is SoftSell free to use?</h3><p>Listing is free. Buyers pay nothing. Sellers pay a 4% success fee only when a transaction completes.</p>
<h3>What countries does SoftSell support?</h3><p>SoftSell is available globally. Transactions are denominated in USD.</p>
<hr/>
<h2>For Sellers</h2>
<h3>How long does it take to get listed?</h3><p>The listing wizard takes under 10 minutes. Listings are reviewed and approved within 24 hours of submission.</p>
<h3>Do I need a minimum revenue to list?</h3><p>No. There is no minimum revenue requirement. However, listings with verified revenue receive more buyer interest.</p>
<h3>What does the 4% success fee cover?</h3><p>The success fee covers the cost of the platform, payment processing, transaction support, and the buyer-seller transfer process. It is charged only on completed transactions — never on listings that do not sell.</p>
<h3>Can I list multiple products?</h3><p>Yes. You can list as many products as you like on a single account.</p>
<h3>What if my listing doesn't sell?</h3><p>There is no time limit on listings. Your listing stays live until you sell, remove it, or mark it as sold elsewhere. You are never charged for a listing that does not sell.</p>
<hr/>
<h2>For Buyers</h2>
<h3>Is it free to browse listings?</h3><p>Yes. Browsing all public listings is free.</p>
<h3>Do buyers pay a fee?</h3><p>No. Buyers pay no fees on SoftSell. The seller pays the 4% success fee.</p>
<h3>How do I know the revenue figures are accurate?</h3><p>Listings with a ✓ Verified badge have had their revenue confirmed directly through a payment processor integration (Stripe, Paddle, Lemon Squeezy, or PayPal). Always ask for documentation for listings that are not yet verified.</p>
<h3>Can I speak with the seller before making an offer?</h3><p>Yes. You can message any seller directly through the platform.</p>
<hr/>
<h2>Transactions</h2>
<h3>How does escrow work?</h3><p>When a buyer and seller agree on a price, the buyer deposits the full amount into a third-party escrow account. Once the buyer confirms successful receipt of all assets, escrow releases the funds to the seller. SoftSell's 4% success fee is deducted at this point.</p>
<h3>How long does a typical transaction take?</h3><p>From accepted offer to completed transfer, most transactions take 7–21 days.</p>
<h3>What is a Letter of Intent (LOI)?</h3><p>An LOI is a non-binding document that outlines the agreed price, timeline, and basic terms of the acquisition before a formal purchase agreement is signed. SoftSell generates LOIs automatically when both parties agree to proceed.</p>`
  },
  "about": {
    title: "About SoftSell",
    content: `<p>SoftSell is a marketplace for buying and selling software businesses and digital software assets.</p>
<p>We built SoftSell because the existing acquisition market was broken for most software founders. Established platforms were built for venture-backed startups doing millions in revenue. The indie developer with a $1,500 MRR Chrome extension, the solo founder with a $3,000 MRR WordPress plugin, the no-code builder with a profitable automation tool — they had nowhere to go.</p>
<p>We believe every piece of software has a buyer. And every founder deserves a clean, simple, trustworthy way to exit.</p>
<h3>Our mission</h3>
<p>To become the place where software changes hands — at any size, in any category, for any founder.</p>
<h3>What we support</h3>
<p>SaaS · AI tools · Chrome extensions · WordPress plugins · Shopify apps · Mobile apps · APIs · No-code products · Automation tools · Internal tools · Digital products</p>
<h3>Our principles</h3>
<ul>
<li><strong>Transparency.</strong> Every listing should show real, verifiable numbers. We verify revenue and traffic through direct integrations — not screenshots that can be edited.</li>
<li><strong>Accessibility.</strong> A $2,000 Chrome extension deserves the same quality marketplace as a $200,000 SaaS. There is no minimum listing size.</li>
<li><strong>Simplicity.</strong> Selling a software business should not require a broker, a lawyer, and six months of your life. We automate the LOI, the purchase agreement, the escrow, and the transfer checklist.</li>
<li><strong>Fairness.</strong> We charge 4% on completed sales only. We never charge sellers for listings that don't sell.</li>
</ul>
<h3>Contact</h3>
<p>For general inquiries: hello@softsell.io<br/>
For support: support@softsell.io<br/>
For press: press@softsell.io</p>`
  },
  "pricing": {
    title: "Pricing",
    content: `<p><strong>Simple, transparent pricing. No surprises.</strong></p>
<h2>For Sellers</h2>
<table><thead><tr><th>Feature</th><th>Price</th></tr></thead><tbody>
<tr><td>Create an account</td><td>Free</td></tr>
<tr><td>List a software product</td><td>Free</td></tr>
<tr><td>Receive and respond to offers</td><td>Free</td></tr>
<tr><td>Message buyers</td><td>Free</td></tr>
<tr><td>Revenue Verification Badge</td><td>$49 one-time</td></tr>
<tr><td>Traffic Verification Badge</td><td>$49 one-time</td></tr>
<tr><td>Full Verification Bundle</td><td>$79 one-time</td></tr>
<tr><td>Featured Listing (30 days)</td><td>$99</td></tr>
<tr><td>Homepage Spotlight (7 days)</td><td>$149</td></tr>
<tr><td>Newsletter Feature (1 issue)</td><td>$79</td></tr>
<tr><td><strong>Success fee on completed sale</strong></td><td><strong>4% of sale price</strong></td></tr>
</tbody></table>
<p>The 4% success fee is the only fee charged on a completed transaction. You receive 96% of the agreed sale price.</p>
<h3>Example:</h3>
<table><thead><tr><th>Sale Price</th><th>SoftSell Fee (4%)</th><th>You Receive</th></tr></thead><tbody>
<tr><td>$5,000</td><td>$200</td><td>$4,800</td></tr>
<tr><td>$10,000</td><td>$400</td><td>$9,600</td></tr>
<tr><td>$25,000</td><td>$1,000</td><td>$24,000</td></tr>
<tr><td>$50,000</td><td>$2,000</td><td>$48,000</td></tr>
<tr><td>$100,000</td><td>$4,000</td><td>$96,000</td></tr>
</tbody></table>
<hr/>
<h2>For Buyers</h2>
<table><thead><tr><th>Feature</th><th>Price</th></tr></thead><tbody>
<tr><td>Create an account</td><td>Free</td></tr>
<tr><td>Browse all listings</td><td>Free</td></tr>
<tr><td>Save listings to watchlist</td><td>Free</td></tr>
<tr><td>Contact sellers</td><td>Free</td></tr>
<tr><td>Make offers</td><td>Free</td></tr>
<tr><td><strong>Complete a purchase</strong></td><td><strong>Free (no buyer fee)</strong></td></tr>
</tbody></table>
<h3>Are there any hidden fees?</h3>
<p>No. The prices listed above are the only fees we charge. There are no account fees, no monthly subscriptions for buyers or sellers, and no fees for listings that do not sell.</p>`
  },
  "careers": {
    title: "Careers",
    content: `<p><strong>Work at SoftSell</strong></p>
<p>We're building the marketplace where software businesses change hands. We're a small, remote team that moves fast and cares about doing things properly.</p>
<h3>Our values</h3>
<ul>
<li><strong>We ship.</strong> We don't over-plan. We build, put it in front of users, learn, and improve.</li>
<li><strong>We write clearly.</strong> Good communication is a core skill for everyone on the team, not just marketers.</li>
<li><strong>We're honest.</strong> With users, with each other, and about what we don't know.</li>
<li><strong>We take responsibility.</strong> If something breaks, we fix it and figure out why it broke.</li>
</ul>
<h3>Open Roles</h3>
<p>We don't have open roles listed at this time. If you're a talented engineer, designer, or operator who cares about the indie software ecosystem, we'd still like to hear from you.</p>
<p>Send a short email to careers@softsell.io with:</p>
<ul>
<li>What you do and what you're best at</li>
<li>Something you've built, shipped, or fixed that you're proud of</li>
<li>Why SoftSell specifically</li>
</ul>
<p>We read every email.</p>`
  },
  "terms": {
    title: "Terms of Service",
    content: `<p><em>Last updated: January 1, 2025</em></p>
<p>Please read these Terms of Service carefully before using SoftSell. By creating an account or using any part of the Platform, you agree to be bound by these Terms.</p>
<h3>1. Acceptance of Terms</h3>
<p>By accessing or using SoftSell, you confirm that you are at least 18 years of age, that you have the legal capacity to enter into binding agreements, and that you agree to these Terms in full.</p>
<h3>2. Description of Service</h3>
<p>SoftSell is an online marketplace that allows sellers to list software businesses and digital software assets for sale, and allows buyers to browse, evaluate, and purchase those listings. SoftSell facilitates the connection between buyers and sellers but is not a party to any transaction between them.</p>
<h3>3. Account Registration</h3>
<p>You must create an account to list software, make offers, or message other users. You agree to provide accurate, complete, and current information. You are responsible for maintaining the security of your account credentials. Contact support@softsell.io immediately if you believe your account has been compromised.</p>
<h3>4. Seller Obligations</h3>
<p>As a seller, you represent and warrant that: (a) You are the legal owner of the software you are listing; (b) All listing information is accurate and complete; (c) You are not listing software that infringes on third-party intellectual property rights; (d) You will not misrepresent revenue, traffic, customer counts, or any other metrics.</p>
<h3>5. Buyer Obligations</h3>
<p>As a buyer, you represent that you have the financial means to complete any offer you make, will conduct your own due diligence before completing a purchase, and will not make offers in bad faith.</p>
<h3>6. Fees and Payments</h3>
<p>Listing on SoftSell is free. SoftSell charges sellers a success fee of 4% of the final agreed sale price on completed transactions. Optional paid services are non-refundable once delivered. SoftSell may modify its fee structure with at least 30 days notice.</p>
<h3>7. Escrow</h3>
<p>SoftSell uses a third-party escrow service to hold buyer funds during transactions. Funds are released to the seller only after the buyer confirms successful receipt of all agreed assets. SoftSell is not the escrow holder and is not liable for the acts or omissions of the escrow provider.</p>
<h3>8. Prohibited Activities</h3>
<p>You agree not to: (a) List software you do not own; (b) Misrepresent material facts; (c) Circumvent platform fees by transacting outside SoftSell after initial contact here; (d) Harass or threaten other users; (e) Use automated tools to scrape or extract data; (f) Engage in any illegal or fraudulent activity.</p>
<h3>9. Limitation of Liability</h3>
<p>SoftSell's total aggregate liability to any user shall not exceed the greater of $100 USD or the total fees paid by that user in the 12 months preceding the claim. SoftSell is not liable for indirect, incidental, special, consequential, or punitive damages.</p>
<h3>10. Disclaimers</h3>
<p>The Platform is provided on an "as is" and "as available" basis without warranties of any kind, express or implied.</p>
<h3>11. Indemnification</h3>
<p>You agree to indemnify and hold harmless SoftSell and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses arising out of your use of the Platform or violation of these Terms.</p>
<h3>12. Governing Law</h3>
<p>These Terms are governed by applicable law. If any provision is found to be unenforceable, the remaining provisions will continue in full force and effect.</p>
<h3>13. Contact</h3>
<p>For questions about these Terms: legal@softsell.io</p>`
  },
  "privacy": {
    title: "Privacy Policy",
    content: `<p><em>Last updated: January 1, 2025</em></p>
<p>This Privacy Policy explains how SoftSell collects, uses, stores, and shares information when you use the SoftSell platform.</p>
<h3>1. Information We Collect</h3>
<p><strong>Information you provide directly:</strong> Name and email when you register, profile information, listing content, messages, and payment information (processed by third-party providers — we do not store card details).</p>
<p><strong>Information collected automatically:</strong> IP address, browser type, device information, pages visited, clicks, and referral source.</p>
<p><strong>Information from third-party integrations:</strong> When you connect Stripe, Paddle, or other payment processors, we receive read-only access to revenue data. When you connect Google Analytics or Plausible, we receive read-only access to analytics data. You can revoke these integrations at any time from your account settings.</p>
<h3>2. How We Use Your Information</h3>
<p>To operate and maintain the Platform, process listings, facilitate transactions, send transactional emails, send the SoftSell newsletter (unsubscribe any time), detect and prevent fraud, improve the Platform, and comply with legal obligations. We do not sell your personal information to third parties. We do not use your information for targeted advertising.</p>
<h3>3. How We Share Your Information</h3>
<p>When you publish a listing, listing content and your display name are visible to all users. Your email address is never displayed publicly. We use third-party service providers for hosting, email delivery, payment processing, and escrow services. We may disclose information if required by law or to protect safety.</p>
<h3>4. Data Retention</h3>
<p>We retain your account information while your account is active. Account deletion removes your personal information within 30 days, except where required for legal compliance.</p>
<h3>5. Security</h3>
<p>We use industry-standard security including encryption in transit (TLS), secure authentication, and access controls.</p>
<h3>6. Your Rights</h3>
<p>You may request access to, correction of, deletion of, or a portable export of your personal data. Contact privacy@softsell.io. We respond within 30 days.</p>
<h3>7. Cookies</h3>
<p>We use only essential and analytics cookies. We do not use advertising or tracking cookies. See our Cookie Policy for details.</p>
<h3>8. Children's Privacy</h3>
<p>SoftSell is not intended for users under 18. Contact privacy@softsell.io if you believe a minor has created an account.</p>
<h3>9. Contact</h3>
<p>For questions about this Privacy Policy: privacy@softsell.io</p>`
  },
  "cookie-policy": {
    title: "Cookie Policy",
    content: `<p><em>Last updated: January 1, 2025</em></p>
<h3>What are cookies?</h3>
<p>Cookies are small text files placed on your device by websites you visit. They are widely used to make websites work, remember your preferences, and provide information to site owners.</p>
<h3>Essential cookies</h3>
<p>These are required for the Platform to function and cannot be disabled.</p>
<table><thead><tr><th>Cookie</th><th>Purpose</th><th>Duration</th></tr></thead><tbody>
<tr><td>session_id</td><td>Keeps you signed in</td><td>Session</td></tr>
<tr><td>csrf_token</td><td>Protects against cross-site request forgery</td><td>Session</td></tr>
<tr><td>auth_token</td><td>Authenticates your account</td><td>30 days</td></tr>
</tbody></table>
<h3>Analytics cookies</h3>
<p>We use privacy-respecting analytics to understand how the Platform is used. We do not use Google Analytics. Our analytics provider does not track you across other websites and does not sell your data.</p>
<table><thead><tr><th>Cookie</th><th>Purpose</th><th>Duration</th></tr></thead><tbody>
<tr><td>_analytics</td><td>Counts page views and sessions (anonymized)</td><td>1 year</td></tr>
</tbody></table>
<h3>Preference cookies</h3>
<table><thead><tr><th>Cookie</th><th>Purpose</th><th>Duration</th></tr></thead><tbody>
<tr><td>theme</td><td>Remembers your display preferences</td><td>1 year</td></tr>
<tr><td>filters</td><td>Remembers your last search filters</td><td>30 days</td></tr>
</tbody></table>
<h3>What we do NOT use cookies for</h3>
<ul>
<li>We do not use advertising or retargeting cookies</li>
<li>We do not use third-party tracking pixels</li>
<li>We do not share cookie data with advertisers</li>
<li>We do not track you across other websites</li>
</ul>
<h3>How to control cookies</h3>
<ul>
<li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies and other site data</li>
<li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data</li>
<li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
<li><strong>Edge:</strong> Settings → Cookies and Site Permissions</li>
</ul>
<p>Disabling essential cookies will prevent you from signing in to SoftSell.</p>
<h3>Contact</h3>
<p>For questions about cookies, contact privacy@softsell.io</p>`
  }
};

function ContentPage({ pageKey, setPage }) {
  const data = PAGE_CONTENTS[pageKey];
  if (!data) return null;
  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: "64px 24px 100px", fontFamily: "'Inter', sans-serif" }}>
      <button
        onClick={() => setPage("home")}
        style={{ background: "none", border: "none", color: "var(--muted)", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: ".84rem", padding: "0 0 28px", fontFamily: "'Inter',sans-serif", transition: "color .15s" }}
        onMouseEnter={e => e.currentTarget.style.color = "var(--text)"}
        onMouseLeave={e => e.currentTarget.style.color = "var(--muted)"}
      >
        ← Back to home
      </button>
      <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(1.8rem, 4vw, 2.6rem)", fontWeight: 700, color: "#fff", marginBottom: 40, lineHeight: 1.15 }}>
        {data.title}
      </h1>
      <div className="content-page-body" dangerouslySetInnerHTML={{ __html: data.content }} />
      <style>{`
        .content-page-body { color: #94A3B8; line-height: 1.78; }
        .content-page-body > * + * { margin-top: 16px; }
        .content-page-body h2 { color: #E2E8F0; font-size: 1.55rem; margin-top: 48px !important; font-weight: 700; font-family: 'Space Grotesk', sans-serif; }
        .content-page-body h3 { color: #CBD5E1; font-size: 1.1rem; margin-top: 32px !important; font-weight: 600; }
        .content-page-body p { margin: 0; }
        .content-page-body ul, .content-page-body ol { padding-left: 22px; }
        .content-page-body li + li { margin-top: 7px; }
        .content-page-body strong { color: #F1F5F9; font-weight: 600; }
        .content-page-body em { opacity: .8; }
        .content-page-body table { width: 100%; border-collapse: collapse; background: #161B24; border-radius: 10px; overflow: hidden; border: 1px solid #2A3242; }
        .content-page-body th { background: #1E2533; color: #F8FAFC; padding: 12px 16px; text-align: left; font-size: .78rem; font-weight: 600; text-transform: uppercase; letter-spacing: .07em; border-bottom: 1px solid #2A3242; }
        .content-page-body td { border-bottom: 1px solid #2A3242; padding: 12px 16px; font-size: .88rem; }
        .content-page-body tr:last-child td { border-bottom: none; }
        .content-page-body hr { border: none; border-top: 1px solid #2A3242; margin: 40px 0 !important; }
        .content-page-body a { color: #5B4FE8; text-decoration: none; }
        .content-page-body a:hover { text-decoration: underline; }
      `}</style>
    </div>
  );
}

/* ─── Data ─────────────────────────────────────────────────────── */

const CATS = [
  {Icon:Bot,     name:"AI Tools"},
  {Icon:Monitor, name:"SaaS"},
  {Icon:Smartphone, name:"Mobile Apps"},
  {Icon:Globe,   name:"Chrome Ext"},
  {Icon:Layers,  name:"WordPress"},
  {Icon:ShoppingBag, name:"Shopify"},
  {Icon:Settings,name:"APIs"},
  {Icon:Cpu,     name:"No-Code"},
  {Icon:Package, name:"Digital"},
];
const TICKER_ITEMS = [
  {l:"AiSummarize Pro",a:"$52,000"},{l:"TaskFlow SaaS",a:"$18,400"},
  {l:"CartRecovery WP",a:"$96,000"},{l:"HabitLoop",a:"$33,000"},
  {l:"LogoGen AI",a:"$140,000"},{l:"LinkCompact",a:"$9,800"},
];
// Notifications are loaded from Firestore in the Nav component

/* ─── Utils ────────────────────────────────────────────────────── */
function fmt(n) { return "$"+Number(n).toLocaleString(); }

/* ─── Toast system ─────────────────────────────────────────────── */
function useToast() {
  const [toasts, setToasts] = useState([]);
  const add = (msg, type="info") => {
    const id = Date.now();
    setToasts(t => [...t, {id, msg, type}]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3400);
  };
  return { toasts, add };
}
function ToastStack({ toasts }) {
  const icons = {success:CheckCircle, error:XCircle, info:MessageSquare};
  return (
    <div className="toast-wrap">
      {toasts.map(t => {
        const Icon = icons[t.type] || MessageSquare;
        return <div key={t.id} className={`toast ${t.type}`}><span><Icon size={14}/></span>{t.msg}</div>
      })}
    </div>
  );
}

/* ─── Inject CSS ───────────────────────────────────────────────── */
function InjectCSS() {
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = CSS;
    document.head.appendChild(el);
    return () => el.remove();
  }, []);
  return null;
}

/* ─── NavLink (top-level to avoid React error #130) ───────────── */
function NavLink({ p, label, icon: Icon, page, setPage }) {
  return (
    <button
      className={`nav-link${page === p ? ' active-nav-link' : ''}`}
      onClick={() => setPage(p)}
      style={{display:'flex',alignItems:'center',gap:5}}
    >
      <Icon size={14}/>{label}
    </button>
  );
}

/* ─── Nav ──────────────────────────────────────────────────────── */
function Nav({ page, setPage, user, setUser, saved, toast }) {
  const [drawer, setDrawer] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const notifRef = useRef();
  const unread = notifs.filter(n => n.unread).length;

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, "softsell_notifications"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const data = [];
      snap.forEach(doc => data.push({ id: doc.id, ...doc.data() }));
      setNotifs(data.filter(n => n.userId === user.uid));
    });
    return () => unsub();
  }, [user]);

  useEffect(() => {
    const fn = e => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  return (
    <>
      <nav className="nav">
        <div className="nav-inner">
          <div className="nav-logo" onClick={() => setPage("home")}>
            <ShoppingBag size={20} style={{color:'var(--accent)'}}/>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center', lineHeight: 1, userSelect: 'none', marginLeft: '6px' }}>
              <div style={{ fontSize: '24px', fontWeight: 800, letterSpacing: '-0.5px', fontFamily: '"Inter", system-ui, sans-serif', display: 'flex', alignItems: 'flex-start' }}>
                <span style={{ color: '#ffffff' }}>Soft</span>
                <span style={{ 
                  background: 'linear-gradient(90deg, #4da2ff 0%, #a855f7 100%)', 
                  WebkitBackgroundClip: 'text', 
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  color: '#a855f7'
                }}>Sell</span>
                <sup style={{ fontSize: '11px', color: '#ffffff', fontWeight: 600, marginLeft: '2px', marginTop: '4px' }}>™</sup>
              </div>
              <div style={{ fontSize: '9.5px', color: '#9ca3af', fontWeight: 700, letterSpacing: '0.6px', marginTop: '4px', textTransform: 'uppercase' }}>
                Buy. Sell. Grow Software.
              </div>
            </div>
            <span className="beta">beta</span>
          </div>
          <div className="nav-links">
            <NavLink p="browse" label="Browse" icon={Search} page={page} setPage={setPage} />
            <NavLink p="sell" label="Sell" icon={Tag} page={page} setPage={setPage} />
            <NavLink p="valuation" label="Valuation" icon={Calculator} page={page} setPage={setPage} />
            <button className="nav-cta" style={{background: 'var(--surface2)', color: 'var(--text)', border: '1px solid var(--border)', marginLeft: '4px'}} onClick={() => setPage("builder")}>SoftSell Builder™</button>
            {user ? (
              <>
                <NavLink p="dashboard" label="Dashboard" icon={LayoutDashboard} page={page} setPage={setPage} />
                <NavLink p="messages" label="Messages" icon={MessageSquare} page={page} setPage={setPage} />
                <div style={{position:"relative"}} ref={notifRef}>
                  <button className="nav-icon-btn" onClick={() => setNotifOpen(o => !o)} title="Notifications">
                    <Bell size={17}/>{unread > 0 && <span className="nav-badge" />}
                  </button>
                  {notifOpen && <NotifPanel onClose={() => setNotifOpen(false)} setPage={setPage} notifs={notifs} />}
                </div>
                <button className="nav-icon-btn" onClick={() => setPage("saved")} title="Saved" style={{position:'relative'}}>
                  <Bookmark size={17}/>{saved.length > 0 && <span style={{position:"absolute",top:4,right:4,width:8,height:8,background:"var(--gold)",borderRadius:"50%",border:"2px solid var(--bg)"}} />}
                </button>
                <button className="nav-cta" onClick={async () => { await signOut(auth); setUser(null); setPage("home"); toast("Signed out","info"); }} style={{display:'flex',alignItems:'center',gap:6}}>
                  <LogOut size={14}/>Sign Out
                </button>
              </>
            ) : (
              <>
                <button className="nav-icon-btn" onClick={() => setPage("saved")} title="Saved"><Bookmark size={17}/></button>
                <button className="nav-link" onClick={() => setPage("login")} style={{display:'flex',alignItems:'center',gap:5}}><LogIn size={14}/>Sign In</button>
                <button className="nav-cta" onClick={() => setPage("register")} style={{display:'flex',alignItems:'center',gap:6}}><Plus size={14}/>Get Started</button>
              </>
            )}
          </div>
          <button className="hamburger" onClick={() => setDrawer(true)}><Menu size={22}/></button>
        </div>
      </nav>

      {drawer && (
        <>
          <div className="drawer-overlay" onClick={() => setDrawer(false)} />
          <div className="drawer">
            <button className="drawer-close" onClick={() => setDrawer(false)}><X size={20}/></button>
            <div style={{fontFamily:'var(--display)',fontSize:'1.1rem',fontWeight:700,color:'#fff',marginBottom:12,display:'flex',alignItems:'center',gap:8}}>
              <ShoppingBag size={18} style={{color:'var(--accent)'}}/> SoftSell
            </div>
            {[{Icon:Home,l:"Home",p:"home"},{Icon:Search,l:"Browse",p:"browse"},{Icon:Calculator,l:"Valuation",p:"valuation"},{Icon:Bookmark,l:"Saved",p:"saved"}].map(({Icon,l,p}) => (
              <button key={p} className="drawer-link" onClick={() => { setPage(p); setDrawer(false); }} style={{display:'flex',alignItems:'center',gap:10}}>
                <Icon size={16}/>{l}
              </button>
            ))}
            {user ? (
              <>
                <button className="drawer-link" onClick={() => { setPage("dashboard"); setDrawer(false); }} style={{display:'flex',alignItems:'center',gap:10}}><LayoutDashboard size={16}/>Dashboard</button>
                <button className="drawer-link" onClick={() => { setPage("messages"); setDrawer(false); }} style={{display:'flex',alignItems:'center',gap:10}}><MessageSquare size={16}/>Messages</button>
                <button className="drawer-link" style={{color:"var(--red)",display:'flex',alignItems:'center',gap:10}} onClick={async () => { await signOut(auth); setUser(null); setPage("home"); setDrawer(false); }}><LogOut size={16}/>Sign Out</button>
              </>
            ) : (
              <>
                <button className="drawer-link" onClick={() => { setPage("login"); setDrawer(false); }} style={{display:'flex',alignItems:'center',gap:10}}><LogIn size={16}/>Sign In</button>
                <button className="drawer-link primary" onClick={() => { setPage("sell"); setDrawer(false); }} style={{display:'flex',alignItems:'center',gap:8}}><Plus size={15}/>List My Software</button>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
}

function BottomNav({ page, setPage, user }) {
  const items = [
    {Icon:Home, label:"Home", p:"home"},
    {Icon:Search, label:"Browse", p:"browse"},
    {Icon:Bookmark, label:"Saved", p:"saved"},
    {Icon:user?LayoutDashboard:LogIn, label:user?"Dash":"Sign In", p:user?"dashboard":"login"},
    {Icon:MessageSquare, label:"Messages", p:"messages"},
  ];
  return (
    <div className="bottom-nav">
      {items.map(({Icon,label,p}) => (
        <button key={p} className={`bottom-nav-item${page===p?" active":""}`} onClick={() => setPage(p)}>
          <Icon size={20} strokeWidth={page===p?2.2:1.8}/>
          {label}
        </button>
      ))}
    </div>
  );
}

function NotifPanel({ onClose, setPage, notifs = [] }) {
  return (
    <div className="notif-panel">
      <div className="notif-header">
        <h4>Notifications</h4>
        <button className="btn btn-ghost btn-sm" onClick={onClose} style={{padding:"4px 8px",fontSize:".72rem"}}>Mark all read</button>
      </div>
      {notifs.length === 0 ? (
        <div className="notif-empty">No notifications yet — offers and messages will appear here</div>
      ) : notifs.map(n => (
        <div key={n.id} className={`notif-item${n.unread?" unread":""}`} onClick={() => { setPage("dashboard"); onClose(); }}>
          {n.unread && <div className="notif-dot" />}
          <div>
            <div className="notif-text">{n.text}</div>
            <div className="notif-time">{n.createdAt?.toDate?.()?.toLocaleDateString?.() || ""}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Ticker ───────────────────────────────────────────────────── */
function Ticker({ sales = [] }) {
  const dynamicItems = sales.map(s => ({ l: s.listingTitle, a: "$" + Number(s.amount).toLocaleString() }));
  const baseItems = dynamicItems.length > 0 ? dynamicItems : TICKER_ITEMS;
  
  // Pad with TICKER_ITEMS if we have too few real sales to scroll smoothly
  let filledItems = [...baseItems];
  if (filledItems.length < 5) {
    filledItems = [...filledItems, ...TICKER_ITEMS].slice(0, 8);
  }
  
  const doubled = [...filledItems, ...filledItems];
  return (
    <div className="ticker">
      <div className="ticker-track">
        {doubled.map((t,i) => (
          <div className="ticker-item" key={i}>
            <span className="tick-badge">SOLD</span>
            <b>{t.l}</b> sold for <b>{t.a}</b>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Listing Card ─────────────────────────────────────────────── */
function LCard({ listing, onClick, saved, onSave, toast }) {
  const isSaved = saved.includes(listing.id);
  const ListingIcon = CATS.find(c => c.name === listing.category)?.Icon || Package;
  return (
    <div className="lcard" onClick={() => onClick(listing)}>
      <div className="lcard-img" style={{background:listing.verified?"linear-gradient(135deg,rgba(91,79,232,.12),var(--bg))":"linear-gradient(135deg,var(--surface2),var(--bg))"}}>
        <span style={{display:'flex',justifyContent:'center',alignItems:'center',width:64,height:64,borderRadius:'50%',background:'var(--surface)',boxShadow:'0 4px 12px rgba(0,0,0,0.1)'}}><ListingIcon size={32} color="var(--text)"/></span>
        {listing.verified && <span className="verified-badge" style={{position:"absolute",top:9,left:9,display:'inline-flex',alignItems:'center',gap:4}}><CheckCircle size={10}/>Verified</span>}
        <button className={`lcard-save${isSaved?" saved":""}`} onClick={e => { e.stopPropagation(); onSave(listing.id); toast(isSaved?"Removed from saved":"Saved to watchlist",isSaved?"info":"success"); }}>
          <Star size={14} fill={isSaved?"currentColor":"none"}/>
        </button>
      </div>
      <div className="lcard-body">
        <div className="lcard-tags">
          {listing.tags.map(t => <span key={t} className="tag tag-accent">{t}</span>)}
        </div>
        <div className="lcard-title">{listing.title}</div>
        <div className="lcard-desc">{listing.desc}</div>
        <div className="lcard-metrics">
          <div className="lm-item"><label>MRR</label><strong>{fmt(listing.mrr)}</strong></div>
          <div className="lm-item"><label>Profit</label><strong>{fmt(listing.profit)}</strong></div>
          <div className="lm-item growth"><label>Growth</label><strong>+{listing.growth}%</strong></div>
          <div className="lm-item price"><label>Asking Price</label><strong>{fmt(listing.price)}</strong></div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  PAGES                                                          */
/* ═══════════════════════════════════════════════════════════════ */

/* ─── Home ─────────────────────────────────────────────────────── */
function HomePage({ setPage, setSelectedListing, saved, onSave, toast, listings = [], sales = [], user }) {
  return (
    <>
      <Ticker sales={sales} />
      {/* HERO */}
      <div className="hero">
        <div className="container">
          <div className="hero-pill"><Rocket size={14} color="var(--accent-light)"/> <span>500+</span> listed · <span>$2.4M</span> traded · <span>4%</span> fee</div>
          <h1>Buy and Sell<br /><em>Software Businesses</em></h1>
          <p className="hero-sub">The marketplace for SaaS, AI tools, apps, Chrome extensions, WordPress plugins, and digital products of any size.</p>
          <div className="hero-actions">
            <button className="btn btn-primary btn-lg" onClick={() => setPage("browse")}>Browse Listings</button>
            <button className="btn btn-secondary btn-lg" onClick={() => setPage("sell")}>List My Software →</button>
          </div>
          <div className="hero-stats">
            {[["500+","Active Listings"],["$2.4M","Total Traded"],["4%","Success Fee"],["24h","Avg Response"]].map(([v,l]) => (
              <div className="hero-stat" key={l}><strong>{v}</strong><label>{l}</label></div>
            ))}
          </div>
        </div>
      </div>

      {/* CATEGORIES */}
      <div className="section" style={{paddingTop:0}}>
        <div className="container">
          <div style={{marginBottom:28}}>
            <div className="sec-eyebrow">Browse by type</div>
            <div className="sec-title">Every kind of software asset</div>
          </div>
          <div className="cat-grid">
            {CATS.map(c => (
              <div key={c.name} className="cat-card" onClick={() => setPage("browse")}>
                <div className="cat-icon" style={{display:'flex',justifyContent:'center',alignItems:'center'}}><c.Icon size={26} color="var(--accent)"/></div>
                <div className="cat-name">{c.name}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FEATURED */}
      <div className="section" style={{background:"var(--surface)",borderTop:"1px solid var(--border)",borderBottom:"1px solid var(--border)"}}>
        <div className="container">
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:28}}>
            <div><div className="sec-eyebrow">Featured this week</div><div className="sec-title">Verified opportunities</div></div>
            <button className="btn btn-ghost btn-sm" onClick={() => setPage("browse")}>View all →</button>
          </div>
          <div className="listings-grid">
            {listings.filter(l=>l.verified).slice(0,3).map(l => (
              <LCard key={l.id} listing={l} onClick={lst=>{setSelectedListing(lst);setPage("listing");}} saved={saved} onSave={onSave} toast={toast} />
            ))}
          </div>
        </div>
      </div>

      {/* HOW IT WORKS */}
      <div className="section">
        <div className="container">
          <div style={{marginBottom:32}}>
            <div className="sec-eyebrow">How it works</div>
            <div className="sec-title">Simple from listing to close</div>
          </div>
          <div className="hiw-grid">
            {[{title:"For Sellers",steps:[["Create Account","Sign up free in 30 seconds."],["List Your Software","Complete our guided wizard."],["Get Offers","Qualified buyers reach out."],["Close the Deal","We guide the transfer."]]},{title:"For Buyers",steps:[["Browse Listings","Filter by revenue, category, tech."],["Analyze Metrics","Verified revenue and traffic data."],["Make an Offer","Direct negotiation with sellers."],["Acquire Software","Guided handover checklist."]]}].map(side => (
              <div className="hiw-side" key={side.title}>
                <h3>{side.title}</h3>
                {side.steps.map(([t,d],i) => (
                  <div className="hiw-step" key={t}><div className="hiw-num">{i+1}</div><div className="hiw-text"><h4>{t}</h4><p>{d}</p></div></div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* PRICING */}
      <div className="section" style={{background:"var(--surface)",borderTop:"1px solid var(--border)"}}>
        <div className="container">
          <div style={{textAlign:"center",marginBottom:32}}>
            <div className="sec-eyebrow">Pricing</div>
            <div className="sec-title">Pay only when you sell</div>
            <div className="sec-sub">Free to list. 4% success fee on completed sales — nothing else hidden.</div>
          </div>
          <div className="pricing-grid">
            {[
              {label:"Free Listing",price:"R0",desc:"Unlimited listings, offer system, direct messaging",btn:"Get Started Free",action:"sell",btnStyle:{background:"var(--surface2)",color:"var(--text)",border:"1px solid var(--border)"}},
              {label:"Verified Badge",price:"R931",desc:"Revenue + traffic verification, ✓ badge on listing",btn:"Get Verified",payfastAmount:"931.00",payfastItem:"SoftSell Verified Badge",btnStyle:{background:"var(--accent)",color:"#fff"}},
              {label:"Featured Placement",price:"R1,881",desc:"Homepage, search boost, newsletter to 8k+ buyers",featured:true,btn:"Get Featured",payfastAmount:"1881.00",payfastItem:"SoftSell Featured Placement",btnStyle:{background:"var(--accent-light)",color:"var(--bg)"}},
              {label:"Success Fee",price:"4%",desc:"Only charged when a sale completes. No upfront cost.",btn:"List for Free",action:"sell",btnStyle:{background:"var(--surface2)",color:"var(--text)",border:"1px solid var(--border)"}}
            ].map(p => (
              <div key={p.label} className={`price-card${p.featured?" featured":""}`}>
                <div className="pc-label">{p.label}</div>
                <div className="pc-price">{p.price}</div>
                <div className="pc-desc">{p.desc}</div>
                {p.payfastAmount ? (
                  <form action="https://www.payfast.co.za/eng/process" method="POST" target="_blank" style={{marginTop:14,width:"100%"}}>
                    <input type="hidden" name="merchant_id" value="19804699" />
                    <input type="hidden" name="merchant_key" value="a5tum0tdbbcnb" />
                    <input type="hidden" name="amount" value={p.payfastAmount} />
                    <input type="hidden" name="item_name" value={p.payfastItem} />
                    <input type="hidden" name="custom_str2" value={user ? user.uid : ""} />
                    <input type="hidden" name="custom_str3" value={p.payfastItem.includes("Verified") ? "verify" : "feature"} />
                    <input type="hidden" name="notify_url" value="https://us-central1-homepro-b60ba.cloudfunctions.net/payfastWebhook" />
                    <input type="hidden" name="return_url" value="https://softsell.letworking.com/?payment=success" />
                    <input type="hidden" name="cancel_url" value="https://softsell.letworking.com/?payment=cancelled" />
                    <button type="submit" className="btn btn-sm" style={{width:"100%",justifyContent:"center",display:"flex",alignItems:"center",gap:6,fontWeight:600,...p.btnStyle}}>{p.btn}</button>
                  </form>
                ) : (
                  <button
                    className="btn btn-sm"
                    style={{marginTop:14,width:"100%",justifyContent:"center",display:"flex",alignItems:"center",gap:6,fontWeight:600,...p.btnStyle}}
                    onClick={() => setPage(p.action)}
                  >{p.btn}</button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* VALUATION NUDGE */}
      <div className="section">
        <div className="container" style={{maxWidth:620,textAlign:"center"}}>
          <div className="sec-title" style={{fontSize:"1.9rem"}}>Not sure what your software is worth?</div>
          <p className="sec-sub" style={{margin:"10px 0 24px"}}>Use our free valuation calculator to get an instant revenue multiple estimate before you list.</p>
          <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
            <button className="btn btn-primary btn-lg" onClick={() => setPage("valuation")}>Free Valuation →</button>
            <button className="btn btn-secondary btn-lg" onClick={() => setPage("sell")}>List My Software</button>
          </div>
        </div>
      </div>
    </>
  );
}

/* ─── Browse ───────────────────────────────────────────────────── */
function BrowsePage({ setPage, setSelectedListing, saved, onSave, toast, listings = [] }) {
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("All");
  const [price, setPrice] = useState("Any");
  const [sort, setSort] = useState("recent");
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  const filtered = listings.filter(l => {
    const q = search.toLowerCase();
    const ms = !q || l.title.toLowerCase().includes(q) || l.desc.toLowerCase().includes(q) || l.tags.some(t=>t.toLowerCase().includes(q));
    const mc = cat==="All" || l.tags.includes(cat) || l.category===cat;
    const mv = !verifiedOnly || l.verified;
    const mp = price==="Any" || (price==="<10k"&&l.price<10000)||(price==="10k-50k"&&l.price>=10000&&l.price<=50000)||(price===">50k"&&l.price>50000);
    return ms && mc && mv && mp;
  }).sort((a,b) => sort==="price-asc"?a.price-b.price:sort==="price-desc"?b.price-a.price:sort==="revenue"?b.mrr-a.mrr:0);

  return (
    <div className="page-pad">
      <div style={{marginBottom:24}}>
        <div className="sec-title">Browse Listings</div>
        <div className="sec-sub">{filtered.length} opportunities available</div>
      </div>
      <div className="filters-bar">
        <input className="search-input" placeholder="Search name, keyword, stack…" value={search} onChange={e=>setSearch(e.target.value)} />
        <select className="f-select" value={cat} onChange={e=>setCat(e.target.value)}>
          <option>All</option>
          {CATS.map(c=><option key={c.name}>{c.name}</option>)}
        </select>
        <select className="f-select" value={price} onChange={e=>setPrice(e.target.value)}>
          <option value="Any">Any Price</option>
          <option value="<10k">Under $10k</option>
          <option value="10k-50k">$10k – $50k</option>
          <option value=">50k">$50k+</option>
        </select>
        <select className="f-select" value={sort} onChange={e=>setSort(e.target.value)}>
          <option value="recent">Recent</option>
          <option value="price-asc">Price ↑</option>
          <option value="price-desc">Price ↓</option>
          <option value="revenue">Revenue</option>
        </select>
        <label className="f-toggle"><input type="checkbox" checked={verifiedOnly} onChange={e=>setVerifiedOnly(e.target.checked)} /> Verified only</label>
      </div>
      {filtered.length === 0 ? (
        <div className="empty-state"><div className="icon" style={{display:'flex',justifyContent:'center'}}><Search size={36} color="var(--muted)"/></div><p>No results for "{search}". Try different keywords or reset filters.</p></div>
      ) : (
        <div className="listings-grid">
          {filtered.map(l => <LCard key={l.id} listing={l} onClick={lst=>{setSelectedListing(lst);setPage("listing");}} saved={saved} onSave={onSave} toast={toast} />)}
        </div>
      )}
    </div>
  );
}

/* ─── Listing Detail ───────────────────────────────────────────── */
function ListingDetailPage({ listing, setPage, user, toast }) {
  const [offer, setOffer] = useState(() => listing ? Math.round(listing.price * 0.88).toLocaleString() : "");
  const [msg, setMsg] = useState("");
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const bars = [68,74,79,83,88,85,90,94,100,97,104,110];
  const max = Math.max(...bars);

  const submitOffer = async () => {
    if (!user) { setPage("register"); return; }
    if (!offer) { toast("Please enter an offer amount", "error"); return; }
    setSubmitting(true);
    try {
      await addDoc(collection(db, "softsell_offers"), {
        listingId: listing.id,
        listingTitle: listing.title,
        sellerId: listing.sellerId || null,
        buyerId: user.uid,
        buyerName: user.displayName || user.email,
        amount: parseFloat(offer.toString().replace(/[^0-9.]/g, "")) || 0,
        message: msg,
        status: "pending",
        createdAt: serverTimestamp(),
      });
      setDone(true);
      toast("Offer sent to seller 🎉", "success");
    } catch (e) {
      console.error(e);
      toast("Failed to send offer. Try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (!listing) return null;
  const multiple = (listing.price / (listing.mrr * 12)).toFixed(1);
  const ListingIcon = CATS.find(c => c.name === listing.category)?.Icon || Package;

  return (
    <div className="page-pad">
      <div className="detail-back" onClick={() => setPage("browse")} style={{display:'inline-flex',alignItems:'center',gap:5}}><ArrowLeft size={14}/>Back to listings</div>
      <div className="detail-head">
        <div className="detail-logo-box" style={{display:'flex',justifyContent:'center',alignItems:'center'}}><ListingIcon size={42} color="var(--text)"/></div>
        <div>
          <div className="detail-title">{listing.title}</div>
          <div className="detail-url" style={{display:'inline-flex',alignItems:'center',gap:4}}><Globe size={13}/>{listing.url}</div>
          <div className="detail-badges">
            {listing.verified && <span className="verified-badge" style={{display:'inline-flex',alignItems:'center',gap:4}}><CheckCircle size={12}/>Revenue Verified</span>}
            <span className="tag tag-accent">{listing.category}</span>
            <span className="tag tag-green">+{listing.growth}% growth</span>
            <span className="tag tag-muted">{multiple}x multiple</span>
          </div>
        </div>
      </div>

      <div className="detail-layout">
        {/* MAIN */}
        <div>
          <div className="detail-card">
            <h3>Overview</h3>
            <p style={{color:"var(--muted)",fontSize:".88rem",lineHeight:1.75}}>{listing.desc}<br/><br/>
            Bootstrapped and profitable with no outside funding. Monthly churn under 2%, strong retention. Full documentation and 30-day handover included. The seller is available for calls during transition.</p>
          </div>

          <div className="detail-card">
            <h3>Key Metrics</h3>
            <div className="metrics-grid">
              {[[fmt(listing.mrr),"Monthly Revenue"],[fmt(listing.profit),"Monthly Profit"],[listing.customers.toLocaleString(),"Customers"],[`+${listing.growth}%`,"Growth MoM"],[`${multiple}x`,"Rev. Multiple"],["<2%","Churn Rate"]].map(([v,l])=>(
                <div className="metric-box" key={l}><div className="val" style={{color:l==="Growth MoM"?"var(--green)":l==="Rev. Multiple"?"var(--accent-light)":"var(--text)"}}>{v}</div><div className="lbl">{l}</div></div>
              ))}
            </div>
          </div>

          <div className="detail-card">
            <h3>Revenue Trend (Last 12 Mo)</h3>
            <div className="bar-chart-wrap">
              <div className="bar-chart">
                {bars.map((b,i)=>(
                  <div key={i} className="bar" style={{height:`${(b/max)*100}%`,background:i===11?"var(--accent)":"rgba(91,79,232,.42)"}} />
                ))}
              </div>
              <div className="chart-labels">
                {["J","F","M","A","M","J","J","A","S","O","N","D"].map((m,i)=><div key={i} className="chart-label">{m}</div>)}
              </div>
            </div>
          </div>

          <div className="detail-card">
            <h3>Technology Stack</h3>
            <div style={{marginBottom:14}}>
              <div style={{fontSize:".74rem",color:"var(--muted)",marginBottom:7}}>FRAMEWORK & TOOLS</div>
              <div className="tech-tags">{listing.tech.map(t=><span key={t} className="tag tag-muted">{t}</span>)}</div>
            </div>
            <div>
              <div style={{fontSize:".74rem",color:"var(--muted)",marginBottom:7}}>HOSTING</div>
              <span className="tag tag-muted">{listing.hosting}</span>
            </div>
          </div>

          <div className="detail-card">
            <h3>Assets Included</h3>
            <div className="assets-list">
              {(() => {
                if (!listing.assets) return null;
                const assetLabels = {code:"📁 Source Code",domain:"🌐 Domain Name",db:"🗄️ Database",docs:"📚 Documentation",social:"📱 Social Accounts",customers:"👥 Customer List",brand:"🎨 Brand Assets",trademark:"™ Trademarks"};
                const included = Object.keys(listing.assets).filter(k => listing.assets[k]);
                if (included.length === 0) return <div className="asset-row" style={{color:"var(--muted)"}}>No assets specified</div>;
                return included.map(k => (
                  <div className="asset-row" key={k}>{assetLabels[k] || k}</div>
                ));
              })()}
            </div>
          </div>

          <div className="detail-card">
            <h3>Reason For Selling</h3>
            <p style={{color:"var(--muted)",fontSize:".88rem",lineHeight:1.75}}>{listing.reason || "The seller hasn't provided a specific reason for selling."}</p>
          </div>
        </div>

        {/* SIDEBAR */}
        <div>
          {done ? (
            <div className="sidebar-card success-card">
              <div className="icon">🎉</div>
              <h2 style={{fontSize:"1.2rem"}}>Offer Sent!</h2>
              <p>The seller typically responds within 24 hours.</p>
              <button className="btn btn-secondary btn-full" onClick={() => setPage("messages")}>View Messages</button>
            </div>
          ) : (
            <div className="sidebar-card">
              <div className="price-big">{fmt(listing.price)}</div>
              <div className="price-note">Asking price · {multiple}x annual revenue</div>
              <div style={{fontSize:".78rem",color:"var(--muted)",marginBottom:6}}>YOUR OFFER</div>
              <input className="offer-field" type="text" placeholder="Enter offer amount" value={offer} onChange={e=>setOffer(e.target.value)} />
              <textarea className="ta" style={{minHeight:72,marginBottom:10,fontSize:".84rem"}} placeholder="Introduce yourself and your plans for this business…" value={msg} onChange={e=>setMsg(e.target.value)} />
              <button className="btn btn-primary btn-full" style={{marginBottom:8,display:'flex',alignItems:'center',justifyContent:'center',gap:6}} onClick={submitOffer} disabled={submitting}>
                <Send size={14}/>{submitting ? "Sending…" : user ? "Submit Offer" : "Sign Up to Make Offer"}
              </button>
              <button className="btn btn-ghost btn-full btn-sm" style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6}}><MessageSquare size={13}/>Ask a Question</button>
            </div>
          )}

          <div className="sidebar-card">
            <div style={{fontSize:".82rem",fontWeight:600,marginBottom:12}}>Seller</div>
            <div className="seller-row">
              {(() => {
                const sName = (user && user.uid === listing.sellerId && user.displayName)
                  ? user.displayName
                  : (listing.sellerName || (listing.sellerEmail ? listing.sellerEmail.split('@')[0].charAt(0).toUpperCase() + listing.sellerEmail.split('@')[0].slice(1) : "Anonymous Seller"));
                const sInitials = sName.substring(0, 2).toUpperCase();
                return (
                  <>
                    <div className="s-avatar">{sInitials}</div>
                    <div>
                      <div style={{fontSize:".86rem",fontWeight:600}}>{sName}</div>
                      <div style={{fontSize:".73rem",color:"var(--muted)"}}>Verified Member</div>
                    </div>
                  </>
                );
              })()}
            </div>
            <div style={{fontSize:".78rem",color:"var(--muted)",marginTop:10,lineHeight:1.6}}>★★★★★ 5.0 · 3 past sales<br/>Responds within 4 hours</div>
          </div>

          <div className="escrow-note">
            <Lock size={13} style={{display:'inline',verticalAlign:'middle',marginRight:6}}/>
            <strong style={{color:"var(--text)"}}>Escrow Protected</strong><br/>
            Funds held in escrow and released only after successful transfer of all assets.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Register ─────────────────────────────────────────────────── */
function RegisterPage({ setPage, setUser, toast }) {
  const [f, setF] = useState({first:"",last:"",email:"",pass:"",terms:false});
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k,v) => setF(p=>({...p,[k]:v}));

  const submit = async () => {
    if (!f.email || !f.pass) { setErr("Email and password are required."); return; }
    if (f.pass.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (!f.terms) { setErr("Please accept the Terms of Service."); return; }
    setLoading(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, f.email, f.pass);
      setUser(cred.user);
      setDone(true);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  if (done) return (
    <div className="form-page">
      <div className="form-card" style={{textAlign:"center"}}>
        <div style={{fontSize:"2.5rem",marginBottom:14,display:'flex',justifyContent:'center'}}><Mail size={52} color="var(--accent)"/></div>
        <h2>Check your email</h2>
        <p className="sub">Verification sent to <strong style={{color:"var(--text)"}}>{f.email}</strong></p>
        <button className="btn btn-primary btn-full" onClick={() => { setPage("dashboard"); toast("Welcome to SoftSell! 🎉","success"); }}>Continue to Dashboard →</button>
      </div>
    </div>
  );

  return (
    <div className="form-page">
      <div className="form-card">
        <h2>Create account</h2>
        <p className="sub">Free forever · 500+ founders already listed</p>
        <form onSubmit={(e) => { e.preventDefault(); submit(); }}>
          <div className="form-row">
            <div className="fg"><label>First Name</label><input className="fi" value={f.first} onChange={e=>set("first",e.target.value)} placeholder="Alex" /></div>
            <div className="fg"><label>Last Name</label><input className="fi" value={f.last} onChange={e=>set("last",e.target.value)} placeholder="Chen" /></div>
          </div>
          <div className="fg"><label>Email</label><input className="fi" type="email" value={f.email} onChange={e=>set("email",e.target.value)} placeholder="you@example.com" /></div>
          <div className="fg"><label>Password</label><input className="fi" type="password" value={f.pass} onChange={e=>set("pass",e.target.value)} placeholder="Min. 8 characters" /></div>
          <label className="chk"><input type="checkbox" checked={f.terms} onChange={e=>set("terms",e.target.checked)} /> I agree to the <a onClick={() => {}} style={{color:"var(--accent-light)",cursor:"pointer"}}>Terms of Service</a> and <a style={{color:"var(--accent-light)",cursor:"pointer"}}>Privacy Policy</a></label>
          {err && <div className="form-error">{err}</div>}
          <button type="submit" className="btn btn-primary btn-full" style={{marginTop:10}} disabled={loading}>{loading ? "Creating account…" : "Create Account"}</button>
        </form>
        <div className="form-foot">Already have an account? <a onClick={() => setPage("login")}>Sign in</a></div>
      </div>
    </div>
  );
}

/* ─── Login ────────────────────────────────────────────────────── */
function LoginPage({ setPage, setUser, toast }) {
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!email || !pass) { setErr("Email and password are required."); return; }
    setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, pass);
      setUser(cred.user);
      setPage("dashboard");
      toast("Welcome back!","success");
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-page">
      <div className="form-card">
        <h2>Welcome back</h2>
        <p className="sub">Sign in to your SoftSell account</p>
        <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
          <div className="fg"><label>Email</label><input className="fi" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@example.com" /></div>
          <div className="fg">
            <label style={{display:"flex",justifyContent:"space-between"}}>Password <a style={{color:"var(--accent-light)",cursor:"pointer",fontWeight:"normal"}}>Forgot?</a></label>
            <input className="fi" type="password" value={pass} onChange={e=>setPass(e.target.value)} placeholder="••••••••" />
          </div>
          {err && <div className="form-error">{err}</div>}
          <button type="submit" className="btn btn-primary btn-full" style={{marginTop:6}} disabled={loading}>{loading ? "Signing in…" : "Sign In"}</button>
        </form>
        <div className="form-foot">No account? <a onClick={() => setPage("register")}>Sign up free</a></div>
      </div>
    </div>
  );
}

/* ─── Valuation Calculator ─────────────────────────────────────── */
function ValuationPage({ setPage }) {
  const [f, setF] = useState({mrr:"",profit:"",growth:"",age:"",churn:"",category:"SaaS"});
  const set = (k,v) => setF(p=>({...p,[k]:v}));
  const mrr = parseFloat(f.mrr) || 0;
  const profit = parseFloat(f.profit) || 0;
  const growth = parseFloat(f.growth) || 0;
  const age = parseFloat(f.age) || 0;
  const churn = parseFloat(f.churn) || 5;

  // Simple multiple model
  let base = f.category === "SaaS" ? 3.5 : f.category === "AI Tool" ? 4.0 : f.category === "Mobile App" ? 2.5 : 2.8;
  base += (growth > 20 ? 0.8 : growth > 10 ? 0.4 : 0);
  base += (churn < 2 ? 0.5 : churn < 5 ? 0.2 : -0.2);
  base += (age > 24 ? 0.3 : age > 12 ? 0.1 : -0.3);
  const annualRevenue = mrr * 12;
  const estimate = Math.round(annualRevenue * base);
  const low = Math.round(estimate * 0.8);
  const high = Math.round(estimate * 1.2);
  const fee = Math.round(estimate * 0.04);

  return (
    <div className="page-pad" style={{maxWidth:680}}>
      <div style={{marginBottom:28}}>
        <div className="sec-eyebrow">Free Tool</div>
        <div className="sec-title">Software Valuation Calculator</div>
        <div className="sec-sub">Get an instant estimate of what your software business is worth based on revenue, growth, and market comparables.</div>
      </div>
      <div className="detail-card">
        <div className="calc-grid">
          <div className="fg"><label>Monthly Revenue ($)</label><input className="fi" type="number" placeholder="e.g. 2500" value={f.mrr} onChange={e=>set("mrr",e.target.value)} /></div>
          <div className="fg"><label>Monthly Profit ($)</label><input className="fi" type="number" placeholder="e.g. 1800" value={f.profit} onChange={e=>set("profit",e.target.value)} /></div>
          <div className="fg"><label>Growth Rate (% per month)</label><input className="fi" type="number" placeholder="e.g. 8" value={f.growth} onChange={e=>set("growth",e.target.value)} /></div>
          <div className="fg"><label>Business Age (months)</label><input className="fi" type="number" placeholder="e.g. 18" value={f.age} onChange={e=>set("age",e.target.value)} /></div>
          <div className="fg"><label>Monthly Churn (%)</label><input className="fi" type="number" placeholder="e.g. 2" value={f.churn} onChange={e=>set("churn",e.target.value)} /></div>
          <div className="fg">
            <label>Category</label>
            <select className="fi" style={{cursor:"pointer"}} value={f.category} onChange={e=>set("category",e.target.value)}>
              {["SaaS","AI Tool","Mobile App","Chrome Extension","WordPress Plugin","No-Code","API","Other"].map(c=><option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {mrr > 0 && (
          <div className="calc-result">
            <div style={{fontSize:".78rem",color:"var(--muted)",marginBottom:6,textTransform:"uppercase",letterSpacing:".07em"}}>Estimated Valuation</div>
            <div className="val">{fmt(estimate)}</div>
            <div className="range">Range: {fmt(low)} – {fmt(high)}</div>
            <div className="breakdown">
              <div><strong>{base.toFixed(1)}x</strong>Revenue Multiple</div>
              <div><strong>{fmt(annualRevenue)}</strong>Annual Revenue</div>
              <div><strong>{fmt(fee)}</strong>4% SoftSell Fee</div>
            </div>
          </div>
        )}

        {mrr === 0 && (
          <div style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:10,padding:16,textAlign:"center",color:"var(--muted)",fontSize:".84rem",marginTop:12}}>
            Enter your monthly revenue above to see your estimate
          </div>
        )}
      </div>

      <div style={{display:"flex",gap:12,flexWrap:"wrap",marginTop:4}}>
        <button className="btn btn-primary" onClick={() => setPage("sell")} style={{display:'flex',alignItems:'center',gap:6}}><Tag size={15}/>List My Software</button>
        <button className="btn btn-ghost" onClick={() => setPage("browse")} style={{display:'flex',alignItems:'center',gap:6}}><Search size={15}/>See Market Comparables</button>
      </div>

      <div style={{marginTop:28,background:"var(--surface)",border:"1px solid var(--border)",borderRadius:13,padding:20}}>
        <div style={{fontFamily:"var(--display)",fontWeight:600,marginBottom:12}}>How we calculate this</div>
        <div style={{fontSize:".83rem",color:"var(--muted)",lineHeight:1.7}}>
          The valuation is based on a revenue multiple applied to annual recurring revenue. The multiple is adjusted for growth rate, churn, business age, and category. SaaS and AI tools command higher multiples (3–5x) due to recurring revenue. Mobile apps and one-time purchase products typically sell at 2–3x. High growth (&gt;20% MoM) adds a premium. Low churn (&lt;2%) increases buyer confidence and therefore price.
        </div>
      </div>
    </div>
  );
}

/* ─── Saved Listings ───────────────────────────────────────────── */
function SavedPage({ saved, setPage, setSelectedListing, onSave, toast, listings = [] }) {
  const items = listings.filter(l => saved.includes(l.id));
  return (
    <div className="page-pad">
      <div style={{marginBottom:24}}>
        <div className="sec-title">Saved Listings</div>
        <div className="sec-sub">{items.length} item{items.length!==1?"s":""} in your watchlist</div>
      </div>
      {items.length === 0 ? (
        <div className="empty-state">
          <div className="icon" style={{display:'flex',justifyContent:'center'}}><Bookmark size={36} color="var(--muted)"/></div>
          <p>No saved listings yet. Click the star on any listing to save it here.</p>
          <button className="btn btn-primary" style={{display:'flex',alignItems:'center',gap:6,margin:'16px auto 0'}} onClick={() => setPage("browse")}><Search size={15}/>Browse Listings</button>
        </div>
      ) : (
        <div className="listings-grid">
          {items.map(l => (
            <LCard key={l.id} listing={l} onClick={lst=>{setSelectedListing(lst);setPage("listing");}} saved={saved} onSave={onSave} toast={toast} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Dashboard ────────────────────────────────────────────────── */
/* ─── Settings Tab ──────────────────────────────────────────────── */
function SettingsTab({ user, setUser, toast }) {
  const [displayName, setDisplayName] = useState(user?.displayName || "");
  const [website, setWebsite] = useState("");
  const [bio, setBio] = useState("");
  const [saving, setSaving] = useState(false);

  // Always sync displayName if user object changes
  useEffect(() => {
    setDisplayName(user?.displayName || "");
  }, [user?.displayName]);

  const saveProfile = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await updateProfile(auth.currentUser, { displayName: displayName.trim() });
      // Use auth.currentUser — never spread the user object, that strips Firebase methods
      setUser(auth.currentUser);
      toast("Profile saved! ✅", "success");
    } catch (e) {
      console.error(e);
      toast("Failed to save profile.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="dash-head"><div className="dash-page-title">Settings</div></div>
      <div className="section-card" style={{maxWidth:480}}>
        <div className="section-card-title">Profile</div>
        <div className="fg">
          <label>Display Name</label>
          <input className="fi" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Your name" />
        </div>
        <div className="fg">
          <label>Email</label>
          <input className="fi" value={user?.email || ""} disabled style={{opacity:.6}} />
        </div>
        <div className="fg">
          <label>Website</label>
          <input className="fi" value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://" />
        </div>
        <div className="fg">
          <label>Bio</label>
          <textarea className="ta" value={bio} onChange={e => setBio(e.target.value)} placeholder="Tell buyers about yourself..." />
        </div>
        <button className="btn btn-primary btn-sm" onClick={saveProfile} disabled={saving}>
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
      <div className="section-card" style={{maxWidth:480,marginTop:16}}>
        <div className="section-card-title">Notifications</div>
        {[["New offer received",true],["New message",true],["Listing approved",true],["Weekly digest",false]].map(([l,d])=>(
          <div key={l} style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,fontSize:".86rem"}}>
            <span>{l}</span>
            <input type="checkbox" defaultChecked={d} style={{accentColor:"var(--accent)",width:16,height:16}} />
          </div>
        ))}
      </div>
    </>
  );
}

function DashboardPage({ user, setUser, setPage, toast, listings = [] }) {
  const [tab, setTab] = useState(() => sessionStorage.getItem("dashTab") || "listings");
  
  useEffect(() => {
    sessionStorage.setItem("dashTab", tab);
  }, [tab]);

  const [offers, setOffers] = useState([]);
  const [analytics, setAnalytics] = useState({ views: 0, clicks: 0, offerRate: "0%", avgResponse: "—" });

  // Load offers from Firestore for this user
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, "softsell_offers"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const data = [];
      snap.forEach(doc => data.push({ id: doc.id, ...doc.data() }));
      setOffers(data.filter(o => o.sellerId === user.uid));
    });
    return () => unsub();
  }, [user]);

  const myListings = listings.filter(l => l.sellerId === user?.uid);

  const navItems = [
    {id:"listings", Icon:Package, label:"My Listings"},
    {id:"offers", Icon:FileText, label:"Offers"},
    {id:"saved-dash", Icon:BookmarkCheck, label:"Watchlist"},
    {id:"analytics", Icon:BarChart2, label:"Analytics"},
    {id:"settings", Icon:Settings, label:"Settings"},
  ];

  return (
    <div className="dash-wrap">
      {/* Sidebar — desktop only */}
      <div className="dash-side">
        <div className="dash-side-user">
          <div className="dash-side-avatar">{user?.name?.[0]?.toUpperCase()||"U"}</div>
          <div>
            <div className="dash-side-name">{user?.name||"User"}</div>
            <div className="dash-side-role">Seller · Founding member</div>
          </div>
        </div>
        <div className="dash-nav">
          {navItems.map(({id, Icon, label}) => (
            <div key={id} className={`dash-nav-item${tab===id?" active":""}`} onClick={() => setTab(id)} style={{display:'flex',alignItems:'center',gap:10}}>
              <Icon size={15}/>{label}
            </div>
          ))}
          <div className="dash-nav-item" onClick={() => setPage("messages")} style={{display:'flex',alignItems:'center',gap:10}}><MessageSquare size={15}/>Messages</div>
        </div>
        <div className="dash-side-bottom">
          <button className="btn btn-primary btn-full btn-sm" onClick={() => setPage("sell")} style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6}}><Plus size={13}/>New Listing</button>
        </div>
      </div>

      {/* Main content */}
      <div className="dash-main">
        {/* Mobile tab bar */}
        <div style={{display:"flex",gap:6,overflowX:"auto",marginBottom:20,paddingBottom:4}}>
          {navItems.map(({id, Icon, label}) => (
            <button key={id} onClick={() => setTab(id)}
              style={{background:tab===id?"var(--accent-dim)":"var(--surface)",border:`1px solid ${tab===id?"var(--accent)":"var(--border)"}`,color:tab===id?"var(--accent-light)":"var(--muted)",padding:"7px 14px",borderRadius:8,fontSize:".78rem",cursor:"pointer",whiteSpace:"nowrap",fontFamily:"'Inter',sans-serif",fontWeight:600,flexShrink:0,display:"flex",alignItems:"center",gap:6}}>
              <Icon size={13}/> {label}
            </button>
          ))}
        </div>

        {/* ── Tab: Listings ── */}
        {tab === "listings" && <>
          <div className="dash-head">
            <div className="dash-page-title">My Listings</div>
            <button className="btn btn-primary btn-sm" onClick={() => setPage("sell")} style={{display:'flex',alignItems:'center',gap:5}}><Plus size={13}/>New Listing</button>
          </div>
          <div className="dash-stats-grid">
            {[["Active", myListings.length.toString(), "your live listings"],["Offers", offers.length.toString(), offers.filter(o=>o.status==="pending").length+" pending"],["Est. Value", myListings.length?"$"+myListings.reduce((s,l)=>s+(Number(l.price)||0),0).toLocaleString():"—","combined asking"],["Watchlisted","—","by buyers"]].map(([l,v,d])=>(
              <div className="ds-card" key={l}><div className="ds-label">{l}</div><div className="ds-val">{v}</div><div className="ds-delta">{d}</div></div>
            ))}
          </div>
          {myListings.length === 0 ? (
            <div className="empty-state">
              <div className="icon" style={{display:'flex',justifyContent:'center'}}><Package size={36} color="var(--muted)"/></div>
              <p>You haven't listed anything yet.</p>
              <button className="btn btn-primary" style={{display:'flex',alignItems:'center',gap:6,margin:'16px auto 0'}} onClick={() => setPage("sell")}><Plus size={15}/>Create Your First Listing</button>
            </div>
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead><tr><th>Product</th><th>MRR</th><th>Asking</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {myListings.map(l => (
                    <tr key={l.id}>
                      <td><div style={{display:"flex",alignItems:"center",gap:8}}>{l.title}</div></td>
                      <td>{l.mrr ? fmt(l.mrr) : "—"}</td>
                      <td>{l.price ? fmt(l.price) : "—"}</td>
                      <td><span className="status-pill sp-live">Live</span></td>
                      <td>
                        <div className="action-btns" style={{alignItems:"center"}}>
                          {!l.verified && (
                            <form action="https://www.payfast.co.za/eng/process" method="POST" target="_blank" style={{margin:0}}>
                              <input type="hidden" name="merchant_id" value="19804699" />
                              <input type="hidden" name="merchant_key" value="a5tum0tdbbcnb" />
                              <input type="hidden" name="amount" value="931.00" />
                              <input type="hidden" name="item_name" value={`Verify: ${l.title}`} />
                              <input type="hidden" name="custom_str1" value={l.id} />
                              <input type="hidden" name="custom_str2" value={user.uid} />
                              <input type="hidden" name="custom_str3" value="verify" />
                              <input type="hidden" name="notify_url" value="https://us-central1-homepro-b60ba.cloudfunctions.net/payfastWebhook" />
                              <input type="hidden" name="return_url" value="https://softsell.letworking.com/?payment=success" />
                              <input type="hidden" name="cancel_url" value="https://softsell.letworking.com/?payment=cancelled" />
                              <button type="submit" className="btn btn-ghost btn-sm" style={{color:"var(--accent-light)"}}>Verify (R931)</button>
                            </form>
                          )}
                          {!l.featured && (
                            <form action="https://www.payfast.co.za/eng/process" method="POST" target="_blank" style={{margin:0}}>
                              <input type="hidden" name="merchant_id" value="19804699" />
                              <input type="hidden" name="merchant_key" value="a5tum0tdbbcnb" />
                              <input type="hidden" name="amount" value="1881.00" />
                              <input type="hidden" name="item_name" value={`Feature: ${l.title}`} />
                              <input type="hidden" name="custom_str1" value={l.id} />
                              <input type="hidden" name="custom_str2" value={user.uid} />
                              <input type="hidden" name="custom_str3" value="feature" />
                              <input type="hidden" name="notify_url" value="https://us-central1-homepro-b60ba.cloudfunctions.net/payfastWebhook" />
                              <input type="hidden" name="return_url" value="https://softsell.letworking.com/?payment=success" />
                              <input type="hidden" name="cancel_url" value="https://softsell.letworking.com/?payment=cancelled" />
                              <button type="submit" className="btn btn-ghost btn-sm" style={{color:"var(--gold)"}}>Feature (R1881)</button>
                            </form>
                          )}
                          <button className="btn btn-ghost btn-sm">Edit</button>
                          <button className="btn btn-ghost btn-sm" style={{color:"var(--red)"}}>Pause</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>}

        {/* ── Tab: Offers ── */}
        {tab === "offers" && <>
          <div className="dash-head"><div className="dash-page-title">Offers</div></div>
          <div className="dash-stats-grid">
            {[["Total", offers.length.toString(), "all time"],["Pending", offers.filter(o=>o.status==="pending").length.toString(), "awaiting response"],["Avg Offer", offers.length ? "$"+Math.round(offers.reduce((s,o)=>s+(Number(o.amount)||0),0)/offers.length).toLocaleString() : "—", "per offer"],["Accepted", offers.filter(o=>o.status==="accepted").length.toString(), "completed"]].map(([l,v,d])=>(
              <div className="ds-card" key={l}><div className="ds-label">{l}</div><div className="ds-val">{v}</div><div className="ds-delta">{d}</div></div>
            ))}
          </div>
          {offers.length === 0 ? (
            <div className="empty-state">
              <div className="icon" style={{display:'flex',justifyContent:'center'}}><FileText size={36} color="var(--muted)"/></div>
              <p>No offers yet. Offers from buyers on your listings will appear here.</p>
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              {offers.map((o) => (
                <div key={o.id} className="section-card">
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12,flexWrap:"wrap",gap:10}}>
                    <div>
                      <div style={{fontWeight:600,fontSize:".94rem"}}>{o.listingTitle || "Your Listing"}</div>
                      <div style={{fontSize:".78rem",color:"var(--muted)",marginTop:2}}>from {o.buyerName || "Anonymous"} · {o.createdAt?.toDate?.()?.toLocaleDateString?.() || ""}</div>
                    </div>
                    <div style={{fontFamily:"var(--display)",fontSize:"1.3rem",fontWeight:700,color:"var(--accent-light)"}}>{fmt(o.amount)}</div>
                  </div>
                  {o.message && <div style={{fontSize:".83rem",color:"var(--muted)",marginBottom:14,background:"var(--bg)",padding:"10px 12px",borderRadius:8,border:"1px solid var(--border)"}}>"{o.message}"</div>}
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    <button className="btn btn-green btn-sm" onClick={() => toast("Offer accepted! Escrow initiated.","success")} style={{display:'flex',alignItems:'center',gap:5}}><CheckCircle size={13}/>Accept</button>
                    <button className="btn btn-secondary btn-sm" style={{display:'flex',alignItems:'center',gap:5}}><CornerDownLeft size={13}/>Counter</button>
                    <button className="btn btn-ghost btn-sm" style={{display:'flex',alignItems:'center',gap:5}}><MessageSquare size={13}/>Message</button>
                    <button className="btn btn-ghost btn-sm" style={{color:"var(--red)",display:'flex',alignItems:'center',gap:5}} onClick={() => toast("Offer declined","info")}><XCircle size={13}/>Decline</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>}

        {/* ── Tab: Watchlist ── */}
        {tab === "saved-dash" && <>
          <div className="dash-head"><div className="dash-page-title">My Watchlist</div></div>
          <div style={{color:"var(--muted)",fontSize:".86rem",marginBottom:16}}>Listings you've saved to watch.</div>
          <div className="saved-grid">
            {listings.slice(0,3).map(l=>(
              <div key={l.id} className="saved-mini" onClick={() => setPage("saved")}>
                <div className="saved-emoji">{l.emoji}</div>
                <div>
                  <div style={{fontSize:".86rem",fontWeight:600}}>{l.title}</div>
                  <div style={{fontSize:".74rem",color:"var(--accent-light)"}}>{fmt(l.price)}</div>
                  <div style={{fontSize:".72rem",color:"var(--muted)",marginTop:1}}>MRR {fmt(l.mrr)}</div>
                </div>
              </div>
            ))}
          </div>
        </>}

        {/* ── Tab: Analytics ── */}
        {tab === "analytics" && <>
          <div className="dash-head"><div className="dash-page-title">Analytics</div></div>
          <div className="dash-stats-grid">
            {[["My Listings", myListings.length.toString(), "active"],["Offers Received", offers.length.toString(), "total"],["Pending Offers", offers.filter(o=>o.status==="pending").length.toString(), "awaiting response"],["Avg Ask", myListings.length ? "$"+Math.round(myListings.reduce((s,l)=>s+(Number(l.price)||0),0)/myListings.length).toLocaleString() : "—", "across listings"]].map(([l,v,d])=>(
              <div className="ds-card" key={l}><div className="ds-label">{l}</div><div className="ds-val">{v}</div><div className="ds-delta">{d}</div></div>
            ))}
          </div>
          {myListings.length === 0 ? (
            <div className="empty-state">
              <div className="icon" style={{display:'flex',justifyContent:'center'}}><BarChart2 size={36} color="var(--muted)"/></div>
              <p>Analytics will appear once you have active listings.</p>
              <button className="btn btn-primary" style={{display:'flex',alignItems:'center',gap:6,margin:'16px auto 0'}} onClick={() => setPage("sell")}><Plus size={15}/>Create a Listing</button>
            </div>
          ) : (
            <div className="section-card">
              <div className="section-card-title">Listing Performance</div>
              <div className="table-scroll" style={{marginTop:12}}>
                <table className="data-table">
                  <thead><tr><th>Title</th><th>Asking</th><th>MRR</th><th>Offers</th></tr></thead>
                  <tbody>
                    {myListings.map(l => (
                      <tr key={l.id}>
                        <td>{l.title}</td>
                        <td>{l.price ? fmt(l.price) : "—"}</td>
                        <td>{l.mrr ? fmt(l.mrr) : "—"}</td>
                        <td>{offers.filter(o=>o.listingId===l.id).length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>}

        {/* ── Tab: Settings ── */}
        {tab === "settings" && <SettingsTab user={user} setUser={setUser} toast={toast} />}
      </div>
    </div>
  );
}

/* ─── Messages ─────────────────────────────────────────────────── */
function MessagesPage({ user }) {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef(null);

  // Load message threads for this user
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, "softsell_threads"), orderBy("updatedAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const data = [];
      snap.forEach(doc => data.push({ id: doc.id, ...doc.data() }));
      const userThreads = data.filter(t => t.participants?.includes(user.uid));
      setThreads(userThreads);
      if (!active && userThreads.length > 0) setActive(userThreads[0]);
    });
    return () => unsub();
  }, [user]);

  // Load messages for active thread
  useEffect(() => {
    if (!active) return;
    const q = query(collection(db, "softsell_threads", active.id, "messages"), orderBy("createdAt", "asc"));
    const unsub = onSnapshot(q, (snap) => {
      const msgs = [];
      snap.forEach(doc => msgs.push({ id: doc.id, ...doc.data() }));
      setMessages(msgs);
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    });
    return () => unsub();
  }, [active]);

  const send = async () => {
    if (!input.trim() || !active || !user) return;
    const text = input.trim();
    setInput("");
    try {
      await addDoc(collection(db, "softsell_threads", active.id, "messages"), {
        text, senderId: user.uid, createdAt: serverTimestamp(), me: true
      });
    } catch(e) { console.error(e); }
  };

  if (!user) return (
    <div className="msgs-wrap" style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'60vh'}}>
      <div className="empty-state"><div className="icon" style={{display:'flex',justifyContent:'center'}}><MessageSquare size={36} color="var(--muted)"/></div><p>Sign in to view your messages.</p></div>
    </div>
  );

  return (
    <div className="msgs-wrap">
      <div className="thread-list">
        <div className="tl-header">Messages</div>
        {threads.length === 0 ? (
          <div style={{padding:"24px 16px",color:"var(--muted)",fontSize:".83rem",textAlign:"center"}}>No conversations yet.<br/>Start by making an offer on a listing.</div>
        ) : threads.map(t => (
          <div key={t.id} className={`thread-item${active?.id===t.id?" active":""}`} onClick={() => setActive(t)}>
            <div className="ti-avatar">{t.otherName?.[0] || "?"}</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div className="ti-name">{t.otherName || "User"}</div>
                <div className="ti-time">{t.updatedAt?.toDate?.()?.toLocaleDateString?.() || ""}</div>
              </div>
              <div className="ti-product">{t.listingTitle || ""}</div>
              <div className="ti-preview">{t.lastMessage || ""}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="chat-wrap">
        {active ? (
          <>
            <div className="chat-top">
              <div className="ti-avatar">{active.otherName?.[0] || "?"}</div>
              <div>
                <div style={{fontWeight:600,fontSize:".9rem"}}>{active.otherName || "User"}</div>
                <div style={{fontSize:".73rem",color:"var(--muted)"}}>Re: {active.listingTitle || "Listing"}</div>
              </div>
            </div>
            <div className="chat-msgs">
              {messages.length === 0 ? (
                <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%',color:'var(--muted)',fontSize:'.84rem'}}>Start the conversation</div>
              ) : messages.map((m, i) => (
                <div key={m.id || i} className={`bubble-wrap${m.senderId===user.uid?" me":" them"}`}>
                  <div className="bubble">{m.text}</div>
                  <div className="bubble-time">{m.createdAt?.toDate?.()?.toLocaleTimeString?.() || ""}</div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            <div className="chat-bottom">
              <input className="chat-fi" placeholder="Type a message…" value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&send()} />
              <button className="btn btn-primary btn-sm" onClick={send} style={{display:'flex',alignItems:'center',gap:5}}><Send size={13}/>Send</button>
            </div>
          </>
        ) : (
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%',color:'var(--muted)',flexDirection:'column',gap:12}}>
            <MessageSquare size={40} color="var(--muted)"/>
            <span style={{fontSize:'.86rem'}}>Select a conversation</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Sell Wizard ──────────────────────────────────────────────── */
const WIZ_STEPS = ["Type","Product","Metrics","Revenue","Traffic","Tech","Sale","Reason","Assets","Review"];
const TYPE_ICONS = {"SaaS":Monitor,"AI Tool":Bot,"Mobile App":Smartphone,"Chrome Ext":Globe,"WordPress":Layers,"Shopify App":ShoppingBag,"API":Settings,"No-Code":Cpu,"Other":Package};

function SellWizard({ setPage, user, toast }) {
  const [step, setStep] = useState(0);
  const [d, setD] = useState({
    type:"SaaS", name:"", url:"", tagline:"", desc:"", mrr:"", profit:"", customers:"", growth:"",
    ask:"", minOffer:"", saleType:"Best Offer", reason:"",
    tech:[], hosting:"",
    assets:{code:true,domain:true,db:true,docs:false,social:false,customers:true,brand:false,trademark:false},
  });
  const [submitted, setSubmitted] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const upd = (k,v) => setD(p=>({...p,[k]:v}));
  const total = WIZ_STEPS.length;
  const pct = `${((step)/total)*100}%`;

  const publish = async () => {
    if (!d.name) { toast("Product name is required", "error"); return; }
    setPublishing(true);
    try {
      await addDoc(collection(db, "softsell_listings"), {
        title: d.name,
        url: d.url || "",
        tagline: d.tagline || "",
        desc: d.desc || "",
        category: d.type,
        tags: [d.type],
        mrr: parseFloat(d.mrr) || 0,
        profit: parseFloat(d.profit) || 0,
        customers: parseInt(d.customers) || 0,
        growth: parseFloat(d.growth) || 0,
        price: parseFloat(d.ask) || 0,
        minOffer: parseFloat(d.minOffer) || 0,
        saleType: d.saleType,
        reason: d.reason || "",
        tech: d.tech,
        hosting: d.hosting || "",
        assets: d.assets,
        sellerId: user.uid,
        sellerEmail: user.email,
        sellerName: user.displayName || user.email.split('@')[0],
        verified: false,
        status: "pending",
        emoji: "🚀",
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
      toast("Listing submitted for review 🎉", "success");
    } catch (e) {
      console.error(e);
      toast("Failed to submit. Please try again.", "error");
    } finally {
      setPublishing(false);
    }
  };

  if (!user) return (
    <div className="form-page">
      <div className="form-card" style={{textAlign:"center"}}>
        <div style={{marginBottom:20,display:'flex',justifyContent:'center'}}><Lock size={52} color="var(--accent)"/></div>
        <h2>Sign in to list</h2>
        <p className="sub">Create your free account to publish a listing.</p>
        <button className="btn btn-primary btn-full" style={{marginBottom:10,display:'flex',alignItems:'center',justifyContent:'center',gap:6}} onClick={() => setPage("register")}><Plus size={15}/>Create Free Account</button>
        <button className="btn btn-secondary btn-full" style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6}} onClick={() => setPage("login")}><LogIn size={15}/>Sign In</button>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="form-page">
      <div className="form-card" style={{textAlign:"center"}}>
        <div style={{marginBottom:16,display:'flex',justifyContent:'center'}}><CheckCircle size={52} color="var(--green)"/></div>
        <h2>Listing Submitted!</h2>
        <p className="sub">Under review — typically approved within 24 hours. You'll get an email once it's live.</p>
        <button className="btn btn-primary btn-full" style={{marginBottom:10}} onClick={() => { setPage("dashboard"); toast("Listing submitted for review","success"); }}>Go to Dashboard</button>
        <button className="btn btn-ghost btn-full" onClick={() => setPage("browse")}>Browse Listings</button>
      </div>
    </div>
  );

  const TYPES = ["SaaS","AI Tool","Mobile App","Chrome Ext","WordPress","Shopify App","API","No-Code","Other"];
  const TECHS = ["React","Next.js","Vue","Svelte","Laravel","Django","Node.js","Python","PHP","Swift","Kotlin","Ruby on Rails","Flutter"];
  const HOSTINGS = ["AWS","Vercel","DigitalOcean","Heroku","Azure","Cloudflare","Firebase","Render","Other"];
  const REASONS = ["Starting new project","Burnout","Career change","Cash needed","Co-founder exit","Time constraints","Profitable exit"];

  const multiple = d.mrr && d.ask ? (parseFloat(d.ask)/(parseFloat(d.mrr)*12)).toFixed(1) : null;

  const steps = [
    /* 0 Type */
    <div key="type">
      <p style={{color:"var(--muted)",fontSize:".86rem",marginBottom:16}}>What kind of software are you selling?</p>
      <div className="opt-grid">
        {TYPES.map(t => {
          const TIcon = TYPE_ICONS[t];
          return (
            <div key={t} className={`opt-card${d.type===t?" sel":""}`} onClick={() => upd("type",t)}>
              <div className="opt-icon" style={{display:'flex',justifyContent:'center'}}><TIcon size={26} color={d.type===t?"var(--accent)":"var(--muted)"}/></div>
              <div className="opt-label">{t}</div>
            </div>
          );
        })}
      </div>
    </div>,

    /* 1 Product */
    <div key="product">
      {[["Product Name","name","text","e.g. MetricsPulse"],["Website URL","url","url","https://yourapp.com"],["Tagline","tagline","text","One-sentence description"]].map(([l,k,t,ph])=>(
        <div className="fg" key={k}><label>{l}</label><input className="fi" type={t} placeholder={ph} value={d[k]||""} onChange={e=>upd(k,e.target.value)} /></div>
      ))}
      <div className="fg"><label>Description</label><textarea className="ta" placeholder="Describe your product, target users, business model, and what makes it valuable…" value={d.desc} onChange={e=>upd("desc",e.target.value)} /></div>
    </div>,

    /* 2 Metrics */
    <div key="metrics">
      <div className="calc-grid">
        {[["Monthly Revenue ($)","mrr"],["Monthly Profit ($)","profit"],["Total Customers","customers"],["Growth Rate (% / mo)","growth"]].map(([l,k])=>(
          <div className="fg" key={k}><label>{l}</label><input className="fi" type="number" placeholder="0" value={d[k]} onChange={e=>upd(k,e.target.value)} /></div>
        ))}
      </div>
      <div className="val-tip" style={{display:'flex',alignItems:'center',gap:6}}><AlertCircle size={14} color="var(--green)"/> Listings with verified metrics get 3× more buyer inquiries. Connect Stripe or Paddle in the next step.</div>
    </div>,

    /* 3 Revenue verification */
    <div key="revenue">
      <p style={{color:"var(--muted)",fontSize:".85rem",marginBottom:14}}>Connect a payment processor to auto-verify revenue and earn a verified badge.</p>
      {[[CreditCard,"Stripe","Connect your Stripe account"],[DollarSign,"Lemon Squeezy","For digital products"],[TrendingUp,"Paddle","SaaS & subscriptions"],[CircleDollarSign,"PayPal","General payments"]].map(([PIcon,n,sub])=>(
        <div className="int-card" key={n}>
          <div className="left"><PIcon size={20} color="var(--accent)"/><div><div style={{fontWeight:500,fontSize:".9rem"}}>{n}</div><div style={{fontSize:".74rem",color:"var(--muted)"}}>{sub}</div></div></div>
          <button className="btn btn-secondary btn-sm" onClick={() => toast(`${n} connection flow would open here`,"info")}>Connect</button>
        </div>
      ))}
      <button className="btn btn-ghost btn-sm" style={{marginTop:6}}>Skip — add verification later</button>
    </div>,

    /* 4 Traffic */
    <div key="traffic">
      <p style={{color:"var(--muted)",fontSize:".85rem",marginBottom:14}}>Connect analytics to verify traffic data.</p>
      {[["📊","Google Analytics","Universal & GA4"],["📈","Plausible","Privacy-first"],["🦅","Fathom","Simple analytics"]].map(([ico,n,sub])=>(
        <div className="int-card" key={n}>
          <div className="left"><span style={{fontSize:"1.25rem"}}>{ico}</span><div><div style={{fontWeight:500,fontSize:".9rem"}}>{n}</div><div style={{fontSize:".74rem",color:"var(--muted)"}}>{sub}</div></div></div>
          <button className="btn btn-secondary btn-sm" onClick={() => toast(`${n} connection flow would open here`,"info")}>Connect</button>
        </div>
      ))}
      <button className="btn btn-ghost btn-sm" style={{marginTop:6}}>Skip for now</button>
    </div>,

    /* 5 Tech */
    <div key="tech">
      <div className="fg">
        <label>Tech Stack (select all that apply)</label>
        <div className="ms-wrap">
          {TECHS.map(t=>(
            <div key={t} className={`ms-chip${d.tech.includes(t)?" sel":""}`} onClick={() => upd("tech",d.tech.includes(t)?d.tech.filter(x=>x!==t):[...d.tech,t])}>{t}</div>
          ))}
        </div>
      </div>
      <div className="fg">
        <label>Hosting</label>
        <div className="ms-wrap">
          {HOSTINGS.map(h=>(
            <div key={h} className={`ms-chip${d.hosting===h?" sel":""}`} onClick={() => upd("hosting",h)}>{h}</div>
          ))}
        </div>
      </div>
    </div>,

    /* 6 Sale details */
    <div key="sale">
      <div className="calc-grid">
        <div className="fg"><label>Asking Price ($)</label><input className="fi" type="number" placeholder="25000" value={d.ask} onChange={e=>upd("ask",e.target.value)} /></div>
        <div className="fg"><label>Minimum Offer ($)</label><input className="fi" type="number" placeholder="18000" value={d.minOffer} onChange={e=>upd("minOffer",e.target.value)} /></div>
      </div>
      <div className="fg">
        <label>Sale Type</label>
        <div className="opt-grid" style={{gridTemplateColumns:"repeat(3,1fr)",marginTop:8}}>
          {[[Tag,"Fixed Price"],[DollarSign,"Best Offer"],[Gavel,"Auction"]].map(([SaleIcon,t])=>(
            <div key={t} className={`opt-card${d.saleType===t?" sel":""}`} onClick={() => upd("saleType",t)}>
              <div className="opt-icon" style={{display:'flex',justifyContent:'center'}}><SaleIcon size={24} color={d.saleType===t?"var(--accent)":"var(--muted)"}/></div>
              <div className="opt-label" style={{fontSize:".76rem"}}>{t}</div>
            </div>
          ))}
        </div>
      </div>
      {multiple && (
        <div className={`val-tip`} style={{background:parseFloat(multiple)<2?"var(--red-dim)":"var(--green-dim)",borderColor:parseFloat(multiple)<2?"rgba(239,68,68,.25)":"rgba(34,197,94,.25)"}}>
          {parseFloat(multiple) < 2 ? "⚠️ " : "✅ "}<strong style={{color:"var(--text)"}}>Valuation check:</strong> {multiple}x annual revenue multiple — {parseFloat(multiple)<2?"below market (3–5x). Consider raising your ask.":parseFloat(multiple)>6?"above market avg. May slow buyer interest.":"in the typical market range for "+d.type+". 👍"}
        </div>
      )}
    </div>,

    /* 7 Reason */
    <div key="reason">
      <div className="fg"><label>Why are you selling?</label><textarea className="ta" style={{minHeight:110}} placeholder="e.g. I'm starting a new venture and want to find this product a great home…" value={d.reason} onChange={e=>upd("reason",e.target.value)} /></div>
      <div className="ms-wrap">
        {REASONS.map(r=>(
          <div key={r} className="ms-chip" style={{cursor:"pointer"}} onClick={() => upd("reason",d.reason?d.reason+(d.reason.endsWith(".")?"":", ")+r:r)}>{r}</div>
        ))}
      </div>
    </div>,

    /* 8 Assets */
    <div key="assets">
      <p style={{color:"var(--muted)",fontSize:".85rem",marginBottom:14}}>What's included in the sale?</p>
      <div className="assets-check-grid">
        {[["code","📁 Source Code"],["domain","🌐 Domain Name"],["db","🗄️ Database"],["docs","📚 Documentation"],["social","📱 Social Accounts"],["customers","👥 Customer List"],["brand","🎨 Brand Assets"],["trademark","™ Trademarks"]].map(([k,label])=>(
          <label className="asset-chk" key={k}>
            <input type="checkbox" checked={d.assets[k]} onChange={e=>upd("assets",{...d.assets,[k]:e.target.checked})} />
            {label}
          </label>
        ))}
      </div>
    </div>,

    /* 9 Review */
    <div key="review">
      <p style={{color:"var(--muted)",fontSize:".86rem",marginBottom:16}}>Review before publishing. You can edit this later.</p>
      <table className="review-tbl">
        <tbody>
          {[["Type",d.type],["Product",d.name||"—"],["URL",d.url||"—"],["MRR",d.mrr?"$"+Number(d.mrr).toLocaleString():"—"],["Profit",d.profit?"$"+Number(d.profit).toLocaleString():"—"],["Asking","$"+(Number(d.ask)||0).toLocaleString()],["Multiple",multiple?`${multiple}x ARR`:"—"],["Sale Type",d.saleType],["Tech",d.tech.join(", ")||"—"],["Hosting",d.hosting||"—"]].map(([l,v])=>(
            <tr key={l}><td>{l}</td><td>{v}</td></tr>
          ))}
        </tbody>
      </table>
    </div>,
  ];

  return (
    <div className="wizard-wrap">
      <div className="wiz-prog-track"><div className="wiz-prog-fill" style={{width:pct}} /></div>
      <div className="wiz-step-info">Step {step+1} of {total} — {WIZ_STEPS[step]}</div>
      <div className="wiz-title">List Your {step===0?"Software":d.type}</div>
      <div className="wiz-sub">{step===0?"Let's get started. What kind of software are you selling?":step===9?"Almost done — publish when ready.":"Free to list. 4% fee only when you sell."}</div>
      <div className="wiz-card">{steps[step]}</div>
      <div className="wiz-nav">
        <button className="btn btn-ghost btn-sm" onClick={() => step===0?setPage("home"):setStep(s=>s-1)} style={{display:'flex',alignItems:'center',gap:5}}>
          {step===0?<><X size={13}/>Cancel</>:<><ArrowLeft size={13}/>Back</>}
        </button>
        <div style={{display:"flex",gap:8}}>
          {step===total-1 ? (
            <>
              <button className="btn btn-secondary btn-sm" onClick={() => { setPage("dashboard"); toast("Draft saved","info"); }} style={{display:'flex',alignItems:'center',gap:5}}><Bookmark size={13}/>Save Draft</button>
              <button className="btn btn-primary btn-sm" onClick={publish} disabled={publishing} style={{display:'flex',alignItems:'center',gap:5}}><Rocket size={13}/>{publishing ? "Publishing…" : "Publish Listing"}</button>
            </>
          ) : (
            <button className="btn btn-primary btn-sm" onClick={() => setStep(s=>s+1)} style={{display:'flex',alignItems:'center',gap:5}}>Continue<ChevronRight size={13}/></button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Social icon SVGs ─────────────────────────────────────────── */
function InstagramIcon({ size = 18 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5"/>
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/>
    </svg>
  );
}

function TwitterIcon({ size = 18 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"/>
    </svg>
  );
}

function ThreadsIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 192 192" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M141.537 88.988a66.667 66.667 0 0 0-2.518-1.143c-1.482-27.307-16.403-42.94-41.457-43.1h-.34c-14.986 0-27.449 6.396-35.12 18.036l13.779 9.452c5.73-8.695 14.724-10.548 21.348-10.548h.229c8.249.053 14.474 2.452 18.503 7.129 2.932 3.405 4.893 8.11 5.864 14.05-7.314-1.243-15.224-1.626-23.68-1.14-23.82 1.371-39.134 15.264-38.105 34.568.522 9.792 5.4 18.216 13.735 23.719 7.047 4.652 16.124 6.927 25.557 6.412 12.458-.683 22.231-5.436 29.049-14.127 5.178-6.6 8.453-15.153 9.899-25.93 5.937 3.583 10.337 8.298 12.767 13.966 4.132 9.635 4.373 25.468-8.546 38.376-11.319 11.308-24.925 16.2-45.488 16.351-22.809-.169-40.053-7.484-51.259-21.741C35.232 139.966 29.8 120.682 29.6 96c.2-24.682 5.632-43.966 16.145-57.317C56.95 24.425 74.194 17.11 97.003 16.941c22.975.17 40.526 7.52 52.171 21.848 5.71 7.025 10.015 15.86 12.853 26.162l16.147-4.308c-3.44-12.68-8.853-23.606-16.219-32.668C147.036 9.607 125.202.195 97.07 0h-.113C68.882.195 47.292 9.642 32.788 28.08 19.882 44.485 13.224 67.315 13.001 96c.223 28.685 6.88 51.515 19.788 67.92 14.504 18.438 36.094 27.884 64.208 28.08h.113c24.96-.173 42.554-6.708 57.048-21.19 18.963-18.949 18.392-42.759 12.14-57.31-4.484-10.454-13.033-18.945-24.762-24.512Zm-43.096 40.519c-10.44.588-21.286-4.098-21.821-14.135-.396-7.442 5.296-15.746 22.461-16.735 1.966-.114 3.895-.169 5.79-.169 6.235 0 12.068.606 17.37 1.765-1.978 24.702-13.826 28.679-23.8 29.274Z"/>
    </svg>
  );
}
function TikTokIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06Z"/>
    </svg>
  );
}

/* ─── Footer ───────────────────────────────────────────────────── */
function Footer({ setPage }) {
  const socials = [
    {
      label: "Instagram",
      href: "https://www.instagram.com/letworking_?igsh=Zm45cXAxaG82Z3Jo&utm_source=qr",
      cls: "insta",
      icon: <InstagramIcon size={16} />,
    },
    {
      label: "Threads",
      href: "https://www.threads.com/@letworking_?igshid=NTc4MTIwNjQ2YQ==",
      cls: "threads",
      icon: <ThreadsIcon size={16} />,
    },
    {
      label: "X (Twitter)",
      href: "https://x.com/letworking_?s=11",
      cls: "x-tw",
      icon: <TwitterIcon size={16} />,
    },
    {
      label: "TikTok",
      href: "https://www.tiktok.com/@mostore_tech",
      cls: "tiktok",
      icon: <TikTokIcon size={16} />,
    },
  ];

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-grid">
          <div>
            <div className="footer-logo">Soft<span>Sell</span></div>
            <div className="footer-desc">The marketplace for buying and selling software businesses of any size — SaaS, AI tools, apps, plugins, extensions, and more.</div>
            <div className="social-links">
              {socials.map(({ label, href, cls, icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={label}
                  className={`social-link ${cls}`}
                  aria-label={label}
                >
                  {icon}
                </a>
              ))}
            </div>
          </div>
          <div className="footer-col">
            <h4>Marketplace</h4>
            {[["Browse Listings","browse"],["Sell Software","sell-landing"],["Valuation Tool","valuation"],["Featured","featured-listings"]].map(([l,p])=><a key={l} onClick={()=>setPage(p)}>{l}</a>)}
          </div>
          <div className="footer-col">
            <h4>Resources</h4>
            {[["How It Works","how-it-works"],["Buyer Guide","buyer-guide"],["Seller Guide","seller-guide"],["Blog","blog"],["FAQ","faq"]].map(([l,p])=><a key={l} onClick={()=>setPage(p)}>{l}</a>)}
          </div>
          <div className="footer-col">
            <h4>Company</h4>
            {[["About","about"],["Pricing","pricing"],["Careers","careers"],["Terms","terms"],["Privacy","privacy"],["Cookies","cookie-policy"]].map(([l,p])=><a key={l} onClick={()=>setPage(p)}>{l}</a>)}
          </div>
        </div>
        <div className="footer-bottom">
          <span>© 2026 SoftSell · Buy and sell software businesses. Letworking™ Powered by MoStore.</span>
          <div className="social-links">
            {socials.map(({ label, href, cls, icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                title={label}
                className={`social-link ${cls}`}
                aria-label={label}
              >
                {icon}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  ROOT APP                                                        */
/* ═══════════════════════════════════════════════════════════════ */
export default function App() {
  const [page, setPage] = useState(() => {
    const hash = window.location.hash.slice(1);
    return hash || "home";
  });

  useEffect(() => {
    window.location.hash = page;
  }, [page]);

  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [saved, setSaved] = useState([]);
  const { toasts, add: toast } = useToast();
  const [listings, setListings] = useState([]);
  const [sales, setSales] = useState([]);

  // Wait for Firebase to restore the session before rendering anything.
  // Without this guard the app renders with user=null on every page refresh,
  // which makes the user appear "logged out" for a fraction of a second
  // (or permanently if code reacts to user=null by redirecting).
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const q = query(collection(db, "softsell_listings"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const data = [];
      snap.forEach(doc => {
        data.push({ id: doc.id, ...doc.data() });
      });
      setListings(data);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    // Fetch recent 50 offers and filter for accepted ones to avoid needing a composite index
    const q = query(collection(db, "softsell_offers"), orderBy("createdAt", "desc"), limit(50));
    const unsub = onSnapshot(q, (snap) => {
      const data = [];
      snap.forEach(doc => {
        const d = doc.data();
        if (d.status === "accepted") {
          data.push({ id: doc.id, ...d });
        }
      });
      setSales(data);
    });
    return () => unsub();
  }, []);

  const onSave = (id) => setSaved(s => s.includes(id) ? s.filter(x=>x!==id) : [...s,id]);

  const noFooter = ["messages","dashboard"].includes(page);

  const commonProps = { setPage, user, setUser, saved, onSave, toast, listings, sales };


  // Show a spinner while Firebase restores the session
  if (!authReady) {
    return (
      <>
        <InjectCSS />
        <div style={{
          display:"flex", alignItems:"center", justifyContent:"center",
          minHeight:"100vh", background:"#0D1117", flexDirection:"column", gap:16
        }}>
          <div style={{
            width:40, height:40, border:"3px solid #2A3242",
            borderTopColor:"#5B4FE8", borderRadius:"50%",
            animation:"spin 0.8s linear infinite"
          }}/>
          <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
          <div style={{color:"#64748B", fontSize:".84rem", fontFamily:"'Inter',sans-serif"}}>
            Loading SoftSell…
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="app">
      <InjectCSS />
      <Nav page={page} setPage={setPage} user={user} setUser={setUser} saved={saved} toast={toast} />
      <main style={{flex:1}}>
        {page==="home"      && <HomePage      {...commonProps} setSelectedListing={setSelectedListing} />}
        {page==="browse"    && <BrowsePage    {...commonProps} setSelectedListing={setSelectedListing} />}
        {page==="listing"   && (selectedListing ? <ListingDetailPage listing={selectedListing} setPage={setPage} user={user} toast={toast} /> : <BrowsePage {...commonProps} setSelectedListing={setSelectedListing} />)}
        {page==="saved"     && <SavedPage     saved={saved} setPage={setPage} setSelectedListing={setSelectedListing} onSave={onSave} toast={toast} listings={listings} />}
        {page==="sell"      && <SellWizard    setPage={setPage} user={user} toast={toast} />}
        {page==="valuation" && <ValuationPage setPage={setPage} />}
        {page==="register"  && <RegisterPage  setPage={setPage} setUser={setUser} toast={toast} />}
        {page==="login"     && <LoginPage     setPage={setPage} setUser={setUser} toast={toast} />}
        {page==="dashboard" && <DashboardPage user={user} setUser={setUser} setPage={setPage} toast={toast} listings={listings} />}
        {page==="messages"  && <MessagesPage user={user} />}
        {["sell-landing","featured-listings","how-it-works","buyer-guide","seller-guide","blog","faq","about","pricing","careers","terms","privacy","cookie-policy"].includes(page) && <ContentPage pageKey={page} setPage={setPage} />}
        {page==="builder" && <SoftSellBuilder />}
      </main>
      {!noFooter && <Footer setPage={setPage} />}
      <BottomNav page={page} setPage={setPage} user={user} />
      <ToastStack toasts={toasts} />
    </div>
  );
}
